import Vue from 'vue';
import Vuex, { ActionContext } from 'vuex';
import { auth, db, functions } from '@/firebase';
import _toString from 'lodash/toString';
import _isEmpty from 'lodash/isEmpty';
import _has from 'lodash/has';
import _uniqBy from 'lodash/uniqBy';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _sortBy from 'lodash/sortBy';
import _mapKeys from 'lodash/mapKeys';
import _omit from 'lodash/omit';
import _slice from 'lodash/slice';
import _keys from 'lodash/keys';
import type { ImgsVisualPromptReplaceTarget, ImgsOriginalsMap } from '@/utils/imgsVisualPromptTarget';
import { getRunnitNumNodesLimit } from '@/utils/runnitNodesLimit';
import {
	Session,
	StableDiffusionOffer,
	StableDiffusionModel,
	User,
	Announcement,
	AppOffer,
	HardwareOffer,
	SoftwareOffer,
	DeploymentImage,
	AppKey,
	HardwareKey,
	SoftwareKey,
	StripeCustomer,
	SetupSessionPrefill,
	Team,
	BalanceAccount,
	Workspace,
	Region,
	ModelProviderKey,
	SessionOptions,
	CivitaiModelVersion,
	CivitaiModel,
	SessionBackgroundTask,
	Runnit,
	RunnitNode,
	RunnitNodeDef,
	RunnitNodeRun,
	Company,
	CompanyUserRole,
	TeamUserRole,
	LoadedAsset,
	RunnitNodeRunResult,
	RunnitNodeField,
	RunnitUpload,
	AnnotationSourceReference,
	RunnitsAccessLevel,
	Avatar,
	ClubOffer,
	ClubOfferBenefits,
	CompanyDisplayLabels,
	RunnitNodeRunResultType,
	UploadFileType,
	UserRunnitNodeDefInfo,
	UserWorkflowRunnitInfo,
	RunnitNodeDefTag,
	RunnitKit,
	Fingerprint,
	Model,
	RUNNITS_ACCESS_LEVEL,
	SESSION_STATE,
	SESSION_TYPE,
	STOP_CODE,
	RunnitNodeRunInputValue,
	RunnitNodeStaticFieldsKey,
	RunnitNodeFieldType,
	RUNNIT_NODE_FIELD_TYPE,
	asyncForEach,
	LimitClubAddOn,
} from '@run-diffusion/shared';
import {
	SNACKBAR_STATUS,
	PAST_SESSION_STATES,
} from '@/constants/constants';
import { maybeNotifyAutoTopUpEvent } from '@/store/utils';
import type {
	Query,
	WhereFilterOp,
	OrderByDirection,
	DocumentReference,
	QueryDocumentSnapshot,
	FirebaseUser,
	FirebaseUserCredential,
	FirebaseUserInfo,
} from '@/firebase';
import {
	getCivitaiModelId,
	getCivitaiModelVersionId,
	getSessionSoundsOff,
	nanoIdLowerNum,
	setSessionSoundsOff,
	removeSessionSoundsOff,
	getProxyAdminToken,
	setProxyAdminToken,
	removeProxyAdminToken,
	getProxyAdminIdToken,
	setProxyAdminIdToken,
	removeProxyAdminIdToken,
	getRunnitsOwnerSelection,
	getRunnitsTeamAccessLevel,
	getMobiusEnabled,
	setMobiusEnabled as lsSetMobiusEnabled,
} from '@/utils';
import moment, { Moment } from 'moment-timezone';
import { buildNewVuefireBindingArray, buildNewVuefireBindingObj } from '@/mixins';
import axios, { AxiosResponse } from 'axios';
import {
	NodeRunResultSelectionMode,
	RUNNITS_OWNER_SELECTION,
	SELECTED_IMAGE_MODE,
	SELECTED_PROMPT_VIEW,
} from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import modelUploadModule from './ModelUploadStore';
import featureTourModule, { FeatureTourState } from './featureTourStore';
import { Breakpoint } from 'vuetify/types/services/breakpoint';
import {
	cacheRunnitImageUrlInState,
	cacheRunnitVideoUrlInState,
} from '@/store/runnitMediaCache';
import {
	getCurrentSelectionStateFromHistory,
	getOrderedSelectionStates,
	getSelectionStateByTriggerFromHistory,
} from '@/store/selectionState';

Vue.use(Vuex);

/*
Interfaces
 */
export interface PreloadModelState {
	civitai: {
		model: CivitaiModel,
		modelVersion: CivitaiModelVersion,
	},
}
export interface ModelSelectItemsState {
	civitai: CivitaiModel[]
}
export interface LoaderState {
	show: boolean
	message?: string
}
export interface LoginQueryParamsDataState {
	teamId?: string
	inviteCode?: string
	autoLoginSsoProvider?: string
	acceptedReferralCode?: string
}
export interface AppQueryParamsDataState {
	creatorsClubSignUp?: boolean
	openManageMembership?: boolean
	openManageTeamSubscription?: boolean
}
export interface TeamReportEmbedUrlsDataState {
	questions: Record<string, string>
	dashboards: Record<string, string>
}
export interface UpsellDialogDataState {
	creatorsClub?: boolean
	runnitPlanDetailsData?: {
		open: boolean
		menuId?: string
	}
	runnitIsAtNumRunnitsLimit?: boolean
	runnitCannotDownloadModel?: boolean
	manageEnterprisePlan?: boolean
	noCompanyBudgets?: boolean
	noCompanyReports?: boolean
	noMultiTeams?: boolean
	noEnterpriseNsfwSettings?: boolean
	noTeamRoles?: boolean
	noSso?: boolean
	noPluginsApi?: boolean
}
interface ToolbarSbudServicesState {
	sessionId: string
	downloadCivitaiModels: boolean
	restart: boolean
	getLogs: boolean
	secrets: Record<string, any>
}
interface ResourcesDialogState {
	open: boolean
	appOffer: AppOffer
}
export interface ToolbarState {
	session: Session
	sbudServicesLoading: boolean
	sbudServices: ToolbarSbudServicesState
	fileBrowserOpen: boolean
	sessionBarOpen: boolean
	sessionBarCollapsed: boolean
	serverBuddyOpen: boolean
	accountMenuOpen: boolean
	changeLoginSettingsDialogConfig: {
		open: boolean
		requireSwitchToSsoProviderId?: boolean
	}
	mobileMenuOpen: boolean
	notificationsOpen: boolean
	settingsOpen: boolean
	logoutDialogOpen: boolean
	needReauthenticateDialogOpen: boolean
	runnitsSideNavOpen: boolean
	runnitsSideNavCollapsed: boolean
	userManuallyCollapsedSideNav: boolean
	resourcesDialog: ResourcesDialogState
	showFailedInitMessage: boolean
}
export type RunnitsOwnerSelection = 'USER' | 'TEAM';

export type RunnitSelectionStateType = RunnitNodeRunResultType | UploadFileType
export interface SelectionState {
	triggeringId: string,
	sortOrder: number, // 0 is falsey, if the sort order is 0 then it is not currently active
	nodeRunResultSelectionMode: NodeRunResultSelectionMode
	canSelectTypes: RunnitSelectionStateType[]
	fieldAwaitingImageGallerySelection: RunnitNodeField
	selectedNodeRunsMap: Record<string, {
		// RunnitNodeRun selected state not flattened
		nodeRun: RunnitNodeRun
		results: RunnitNodeRunResult[]
	}>
	selectedNodeRunResults: RunnitNodeRunResult[] // RunnitNodeRun selected state flattened
	selectedLoadedAssets: LoadedAsset[]
	selectedUploads: RunnitUpload[]
	selectedAvatars: Avatar[]
	// IMGS annotate originals, keyed by current image identity, so the Selected Images
	// dialog can flag annotated images and reset them without the field value in scope.
	originalImages?: ImgsOriginalsMap
}
export const emptySelectionState: SelectionState = {
	triggeringId: null,
	sortOrder: 0,
	canSelectTypes: [],
	nodeRunResultSelectionMode: null,
	fieldAwaitingImageGallerySelection: null,
	selectedNodeRunsMap: {},
	selectedNodeRunResults: [],
	selectedLoadedAssets: [],
	selectedUploads: [],
	selectedAvatars: [],
	originalImages: {},
}
export interface RunnitState {
	runnitsOwnerSelection: RunnitsOwnerSelection
	runnitsAccessLevel: RunnitsAccessLevel
	runnit: Runnit
	selectedNodeId: string

	configureRunnitOpen: boolean
	runnitManageAccessOpen: boolean
	runnitDeleteOpen: boolean
	createRunnitTemplateOpen: boolean
	duplicateTemplate: boolean

	settingsDrawerOpen: boolean
	imageGalleryDialogOpen: boolean
	imageGalleryDialogInitialNav: ImageGalleryDialogNav
	selectedImagesDialogOpen: boolean
	imageGalleryDrawerOpen: boolean
	toolsLibraryDialogOpen: boolean
	globalSearchOpen: boolean
	globalSearchTrigger: 'shortcut' | 'button' | null
	fieldsByType: Record<RunnitNodeFieldType, RunnitNodeField[]>
	generatedNegativePrompt: string
	initialHistoryLength: number

	// Runnit draft values
	inputValues: Record<string, RunnitNodeRunInputValue>
	staticInputValues: Record<RunnitNodeStaticFieldsKey, any>

	// creating a new runnit using the settings dialog first
	runnitDraft: Runnit
	runnitDraftNodeRef: RunnitNodeDef

	// Template cloning properties
	cloneFromTemplate: boolean
	templateRunnit: Runnit

	deletedImages: {
		nodeRunResults: RunnitNodeRunResult[],
		uploads: RunnitUpload[],
		avatars: Avatar[],
	}

	// settings
	selectedImageMode: string
	selectedColumns: number
	selectedPromptView: string

	// RunnitNodeRun draft helper properties
	isQueuingDraftRunnitNodeRun: boolean

	promptGeneratorOpen: boolean
	imageSelectOpen: boolean
	// Inpainting Editor properties - field uuid to inpainting state
	inpaintingEditorStates: Record<string, {
		open: boolean
		saving: boolean
		backgroundImageUrl: string | null
		maskUrl: string | null
	}>

	// Visual Prompt Editor properties - field uuid to visual prompt state
	visualPromptEditorStates: Record<string, {
		open: boolean
		saving: boolean
		backgroundImageUrl: string | null
		baseSource?: AnnotationSourceReference | null
		replaceImageTarget: ImgsVisualPromptReplaceTarget | null
		completedReplacement?: {
			upload: RunnitUpload
			replaceImageTarget: ImgsVisualPromptReplaceTarget
		} | null
		// Set by the Selected Images dialog to ask the field owner to revert an image to
		// its original without opening the editor. Routed via field.uuid (like annotate)
		// so it reaches the owner regardless of which surface opened the dialog.
		resetToOriginalRequest?: ImgsVisualPromptReplaceTarget | null
	}>

	// Pose3D Editor properties - field uuid to Pose3DEditor state.
	// assetFile is the user-picked .glb/.gltf/.obj/.stl held in memory only;
	// nothing is persisted server-side until the rendered 2D screenshot is uploaded.
	pose3DEditorStates: Record<string, {
		open: boolean
		saving: boolean
		assetFile: File | null
		assetName: string | null
	}>

	// Image and video caching
	imageCache: Record<string, string>
	videoCache: Record<string, string>

	imageComparisonMode: boolean
	comparisonFieldSourceImageUrls: Record<string, string> // maps fieldDefUuid to sourceImageUrl
	comparisonFieldDefUuid: string | null // Track which field is being compared

	// Timer tracking for loading placeholders
	draftLoadingTimerStartAtMillisMap: Record<string, number> // RunnitNodeRun.id -> milliseconds

	// Token cooldown state (global across tool switches)
	tokenCooldownData: {
		cooldownEndsAt: Date | null
		cooldownData: {
			tokensSpent: number
			tokensRemaining: number
			threshold: number
			resetAt: string
			timeBucket: string
		} | null
		wasBlockedByCooldown: boolean
	}
}
export interface SnackbarState {
	status: 'ERROR' | 'SUCCESS' | 'WARN' | 'INFO'
	timeout?: number
	show?: boolean
	message: string
	snackbarStyle?: string
}
// A single stacked snackbar entry. Each invocation of the `updateSnackbar`
// action becomes its own item with a unique `id` so it can time out and be
// dismissed independently of the others.
export interface SnackbarItem {
	id: number
	status: SnackbarState['status']
	timeout?: number
	message: string
	snackbarStyle?: string
}
// Max number of snackbars kept on screen at once; older ones are dropped.
export const MAX_SNACKBARS = 3;
// Monotonic id generator so each stacked snackbar is uniquely keyed/dismissable.
let snackbarSeq = 0;
const nextSnackbarId = (): number => (snackbarSeq += 1);
interface Triggers {
	// Trigger prompt-selected model changes because I need to trigger every time it's called, (i.e. if it was initialized to null, and I need to set it to null again to clear the model selection among all runnit fields listening)
	promptSelectedModelChangeTrigger: { model: Model, trigger: number }
	reloadSessionUiIframeTrigger: number
	imageZoomResetTrigger: number
	fireInitialPromptGenerationTrigger: number
	createNewTeamTrigger: number
	/** Increment to open the RunDiffusion account / team switcher menu anchored to the left nav control (see RunnitAccountSelectionDialog). */
	openRunnitAccountSelectionMenuTrigger: number
	toggleRunnitWorkflowAllToolsTrigger: { expandAll?: boolean, collapseAll?: boolean, trigger: number }
	screenResizeBreakpointTrigger: Breakpoint
}
export interface ModelState {
	models: Model[]
	model?: Model
	modelDraft?: Model
	modelDialog: {
		open: boolean,
		mode?: 'edit' | 'create',
	}
}
export interface DeviceReportLocal {
	ip: string
	timeZone: string
	fingerprint: Fingerprint
	loginLastAtMoment: Moment
	domainClaimRestriction?: {
		companyId: string
		companyName: string
		normalizedDomain: string
		// The Caller's own Team Accounts inside the owning Company (resolved server-side at
		// sign-in). Empty/absent => Caller is in no Company team yet (drives the "recheck" CTA).
		matchingTeams?: DomainClaimMatchingTeam[]
	} | null
	// Purpose-specific sign-in flag from captureSignInData: true when the signed-in email's domain
	// is a verified claim of an Enterprise-tier company, so the forced new-user survey is skipped.
	skipNewUserSurvey?: boolean
}

export interface DomainClaimMatchingTeam {
	teamId: string
	teamName: string
}

export interface StoredLastPagination {
	// The page number. A table that pages by number restores the page from it;
	// the members directory, which pages by cursor, remembers the number of the
	// page its `cursor` produced, so its footer can still say "26-50" (see
	// rememberMembersDirectoryView).
	page?: number
	sortBy: string[]
	sortDesc: boolean[]
	itemsPerPage: number
	iso: string
	// Where a cursor-paged table had got to, and the filters that produced it.
	// A cursor belongs to one listing, so the two are only meaningful together
	// and are restored together.
	cursor?: string | null
	// The search (`searchQuery`) and the Filters box, field by field. A field can
	// be a list (Boards' owners) or a yes-or-no as well as one value.
	filters?: Record<string, string | string[] | boolean | null>
}
export interface CompanyDashboardState {
	lastCompanyPagination: Record<string, StoredLastPagination> // companyId -> pagination
	lastMembersDirectoryPagination: Record<string, StoredLastPagination> // companyId -> pagination (server-paged members directory)
	lastTeamPagination: Record<string, StoredLastPagination> // teamId -> pagination
	lastTeamMemberPagination: Record<string, Record<string, StoredLastPagination>> // teamId -> teamMemberId -> pagination
}
export interface RunnitBoardDashboardState {
	lastRunnitBoardsPagination: Record<string, StoredLastPagination> // teamId -> pagination
	lastRunnitTemplatesPagination: Record<string, StoredLastPagination> // teamId -> pagination
}

export type StoreLoadingState = true | false | null; // null=HasNotTriedYet | true=Loading | false=DoneLoading
export interface State {
	// State for vuefire loading & binding props
	loadingClubOffers: StoreLoadingState, boundPropsClubOffers: {},
	loadingAppOffers: StoreLoadingState, boundPropsAppOffers: { user: User },
	loadingHardwareOffers: StoreLoadingState, boundPropsHardwareOffers: { user: User },
	loadingSoftwareOffers: StoreLoadingState, boundPropsSoftwareOffers: { user: User },
	loadingDeploymentImages: StoreLoadingState, boundPropsDeploymentImages: { user: User },
	loadingStableDiffusionOffers: StoreLoadingState, boundPropsStableDiffusionOffers: { user: User },
	loadingWorkspaces: StoreLoadingState, boundPropsWorkspaces: { teamId: string },
	loadingLimitNumRunnits: StoreLoadingState, boundPropsLimitNumRunnits: { userId: string, teamId: string, accessLevel: RunnitsAccessLevel, limitClubAddOn: LimitClubAddOn },
	loadingRunnitNodes: StoreLoadingState, boundPropsRunnitNodes: { runnitId: string },
	loadingTrainerNodeDefs: StoreLoadingState, boundPropsTrainerNodeDefs: {},
	loadingPublicRunnitNodeDefs: StoreLoadingState, boundPropsPublicRunnitNodeDefs: {},
	loadingCompanyRunnitNodeDefs: StoreLoadingState, boundPropsCompanyRunnitNodeDefs: {},
	loadingPublicRunnitNodeDefTags: StoreLoadingState, boundPropsPublicRunnitNodeDefTags: {},
	loadingRunnitKits: StoreLoadingState, boundPropsRunnitKits: {},
	loadingDraftRunnitNodeRun: StoreLoadingState, boundPropsDraftRunnitNodeRun: { userId: string, nodeId: string },
	loadingAnnouncements: StoreLoadingState, boundPropsAnnouncements: {},
	loadingUser: StoreLoadingState, boundPropsUser: { authUser: FirebaseUser },
	loadingUserBalanceAccount: StoreLoadingState, boundPropsUserBalanceAccount: { user: User },
	loadingTeam: StoreLoadingState, boundPropsTeam: { teamId: string },
	loadingTeamBalanceAccount: StoreLoadingState, boundPropsTeamBalanceAccount: { teamId: string },
	loadingStripeCustomer: StoreLoadingState, boundPropsStripeCustomer: { user: User },
	loadingStripeTeamsCustomer: StoreLoadingState, boundPropsStripeTeamsCustomer: { teamId: string },
	loadingCurrSessions: StoreLoadingState, boundPropsCurrSessions: { user: User },
	loadingCurrSessionBackgroundTasks: StoreLoadingState, boundPropsCurrSessionBackgroundTasks: { session: Session },
	loadingAdminRegularSessions: StoreLoadingState, boundPropsAdminRegularSessions: {},
	loadingStableDiffusionModels: StoreLoadingState, boundPropsStableDiffusionModels: {},
	loadingSetupSessionPrefill: StoreLoadingState, boundPropsSetupSessionPrefill: { authUser: FirebaseUser, appKey: AppKey },
	loadingRecentUserRunnitNodeDefInfos: StoreLoadingState, boundPropsRecentUserRunnitNodeDefInfos: { user: User },
	loadingRecentUserWorkflowRunnitInfos: StoreLoadingState, boundPropsRecentUserWorkflowRunnitInfos: { user: User, team: Team },
	loadingModels: StoreLoadingState, boundPropsModels: { userId: string, teamId: string, accessLevel: RunnitsAccessLevel },
	loadingCompanyUserRole: StoreLoadingState, boundPropsCompanyUserRole: { userId: string, companyId: string },
	loadingTeamUserRole: StoreLoadingState, boundPropsTeamUserRole: { userId: string, teamId: string },

	// Other state
	companyDashboard: CompanyDashboardState
	runnitBoardDashboard: RunnitBoardDashboardState
	loginQueryParamsData: LoginQueryParamsDataState
	appQueryParamsData: AppQueryParamsDataState
	teamReportEmbedUrlsData: TeamReportEmbedUrlsDataState
	teamReportEmbedUrlsDataLoading: boolean
	hasCheckedAuth: boolean
	upsellDialog: UpsellDialogDataState
	showMqlCalendarDialog: boolean
	authUser: FirebaseUser
	authUserCredential: FirebaseUserCredential
	stripeCustomer: StripeCustomer
	stripeTeamsCustomer: StripeCustomer
	user: User
	userBalanceAccount: BalanceAccount
	team: Team
	teamBalanceAccount: BalanceAccount
	announcements: Announcement[]
	currSessions: Session[]
	currSessionBackgroundTasks: SessionBackgroundTask[]
	pastSessions: Session[]
	pastSessionsLoading: boolean
	pastSessionsCursor: QueryDocumentSnapshot
	workspaces: Workspace[]
	workspacesMap: Record<string, Workspace>
	limitNumRunnits: Runnit[]
	limitNumRunnitsMap: Record<string, Runnit>
	pendingRunnit: { id: string, type: string } | null
	runnitNodes: RunnitNode[]
	runnitNodesMap: Record<string, RunnitNode>
	trainerNodeDefs: RunnitNodeDef[]
	trainerNodeDefsMap: Record<string, RunnitNodeDef>
	publicRunnitNodeDefs: RunnitNodeDef[]
	publicRunnitNodeDefsMap: Record<string, RunnitNodeDef>
	companyRunnitNodeDefs: RunnitNodeDef[]
	companyRunnitNodeDefsMap: Record<string, RunnitNodeDef>
	publicRunnitNodeDefTags: RunnitNodeDefTag[]
	publicRunnitNodeDefTagsMap: Record<string, RunnitNodeDefTag>
	runnitKits: RunnitKit[]
	runnitKitsMap: Record<string, RunnitKit>
	draftRunnitNodeRun: RunnitNodeRun
	runnitState: RunnitState
	selectionStateHistory: Record<string, SelectionState>
	modelsState: ModelState
	clubOffers: ClubOffer[]
	clubOffersMap: Record<string, ClubOffer>
	appOffers: AppOffer[]
	appOffersMap: Record<string, AppOffer>
	hardwareOffers: HardwareOffer[]
	hardwareOffersMap: Record<string, HardwareOffer>
	softwareOffers: SoftwareOffer[]
	softwareOffersMap: Record<string, SoftwareOffer>
	deploymentImages: DeploymentImage[]
	deploymentImagesMap: Record<string, DeploymentImage>
	deploymentImagesByRepoRef: Record<string, DeploymentImage>
	mobiusEnabledRaw: boolean
	adminRegularSessions: Session[]
	stableDiffusionOffers: StableDiffusionOffer[]
	stableDiffusionModels: StableDiffusionModel[]
	stableDiffusionModel: StableDiffusionModel
	setupSessionPrefill: SetupSessionPrefill
	recentUserRunnitNodeDefInfos: UserRunnitNodeDefInfo[]
	recentUserWorkflowRunnitInfos: UserWorkflowRunnitInfo[]
	companyUserRole: CompanyUserRole
	teamUserRole: TeamUserRole
	preloadModel: PreloadModelState
	modelSelectItems: ModelSelectItemsState
	loader: LoaderState
	snackbars: SnackbarItem[]
	toolbar: ToolbarState
	deviceReportLocal: DeviceReportLocal
	realTimeMoment: Moment
	sessionSoundsOff: boolean
	isAdminProxyingUser: boolean
	triggers: Triggers
	dragAndDrop: {
		isDragging: boolean,
		draggedItem: {
			type: string,
			src: string,
			nodeRunResult: any,
			upload: any,
			avatar: any,
		},
		allowDrop: boolean,
		activeDropZones: number
	}
	// Owned by the namespaced featureTour Vuex module — populated when Vuex
	// registers the module, so it's optional from the root state initializer's
	// perspective. The featureTourService.ts helper casts on read.
	featureTour?: FeatureTourState
}

/*
Store
 */
export default new Vuex.Store({
	state () {
		// Get default state values from local storage
		const sessionSoundsOff: boolean = getSessionSoundsOff();
		const proxyAdminToken: string = getProxyAdminToken();
		let runnitsOwnerSelection: RunnitsOwnerSelection = getRunnitsOwnerSelection();
		const runnitsTeamAccessLevel: RunnitsAccessLevel = getRunnitsTeamAccessLevel();
		let runnitsAccessLevel: RunnitsAccessLevel;
		if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
			runnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
			runnitsAccessLevel = runnitsTeamAccessLevel || RUNNITS_ACCESS_LEVEL.SHARED;
		} else {
			runnitsOwnerSelection = RUNNITS_OWNER_SELECTION.USER;
			runnitsAccessLevel = null;
		}

		return {
			loadingClubOffers: null, boundPropsClubOffers: { user: null },
			loadingAppOffers: null, boundPropsAppOffers: { user: null },
			loadingHardwareOffers: null, boundPropsHardwareOffers: { user: null },
			loadingSoftwareOffers: null, boundPropsSoftwareOffers: { user: null },
			loadingDeploymentImages: null, boundPropsDeploymentImages: { user: null },
			loadingStableDiffusionOffers: null, boundPropsStableDiffusionOffers: { user: null },
			loadingWorkspaces: null, boundPropsWorkspaces: { teamId: null },
			loadingLimitNumRunnits: null, boundPropsLimitNumRunnits: { userId: null, teamId: null, accessLevel: null, limitClubAddOn: null },
			loadingRunnitNodes: null, boundPropsRunnitNodes: { runnitId: null },
			loadingTrainerNodeDefs: null, boundPropsTrainerNodeDefs: {},
			loadingPublicRunnitNodeDefs: null, boundPropsPublicRunnitNodeDefs: {},
			loadingCompanyRunnitNodeDefs: null, boundPropsCompanyRunnitNodeDefs: {},
			loadingPublicRunnitNodeDefTags: null, boundPropsPublicRunnitNodeDefTags: {},
			loadingRunnitKits: null, boundPropsRunnitKits: {},
			loadingDraftRunnitNodeRun: null, boundPropsDraftRunnitNodeRun: { userId: null, nodeId: null },
			loadingAnnouncements: null, boundPropsAnnouncements: {},
			loadingUser: null, boundPropsUser: { authUser: null },
			loadingUserBalanceAccount: null, boundPropsUserBalanceAccount: { user: null },
			loadingTeam: null, boundPropsTeam: { teamId: null },
			loadingTeamBalanceAccount: null, boundPropsTeamBalanceAccount: { teamId: null },
			loadingStripeCustomer: null, boundPropsStripeCustomer: { user: null },
			loadingStripeTeamsCustomer: null, boundPropsStripeTeamsCustomer: { teamId: null },
			loadingCurrSessions: null, boundPropsCurrSessions: { user: null },
			loadingCurrSessionBackgroundTasks: null, boundPropsCurrSessionBackgroundTasks: { session: null },
			loadingAdminRegularSessions: null, boundPropsAdminRegularSessions: { user: null },
			loadingStableDiffusionModels: null, boundPropsStableDiffusionModels: {},
			loadingSetupSessionPrefill: null, boundPropsSetupSessionPrefill: { authUser: null, appKey: null },
			loadingRecentUserRunnitNodeDefInfos: null, boundPropsRecentUserRunnitNodeDefInfos: { user: null },
			loadingRecentUserWorkflowRunnitInfos: null, boundPropsRecentUserWorkflowRunnitInfos: { user: null, team: null },
			loadingModels: null, boundPropsModels: { userId: null, teamId: null, accessLevel: null },
			loadingCompanyUserRole: null, boundPropsCompanyUserRole: { userId: null, companyId: null },
			loadingTeamUserRole: null, boundPropsTeamUserRole: { userId: null, teamId: null },

			companyDashboard: {
				lastCompanyPagination: {},
				lastMembersDirectoryPagination: {},
				lastTeamPagination: {},
				lastTeamMemberPagination: {},
			},
			runnitBoardDashboard: {
				lastRunnitBoardsPagination: {},
				lastRunnitTemplatesPagination: {},
			},
			loginQueryParamsData: {},
			appQueryParamsData: {},
			teamReportEmbedUrlsData: null,
			teamReportEmbedUrlsDataLoading: false,
			hasCheckedAuth: false,
			upsellDialog: {},
			showMqlCalendarDialog: false,
			authUser: null,
			authUserCredential: null,
			stripeCustomer: null,
			stripeTeamsCustomer: null,
			user: null,
			userBalanceAccount: null,
			team: null,
			teamBalanceAccount: null,
			workspaces: [],
			workspacesMap: {},
			limitNumRunnits: [],
			limitNumRunnitsMap: {},
			pendingRunnit: null,
			runnitNodes: [],
			runnitNodesMap: {},
			trainerNodeDefs: [],
			trainerNodeDefsMap: {},
			publicRunnitNodeDefs: [],
			publicRunnitNodeDefsMap: {},
			companyRunnitNodeDefs: [],
			companyRunnitNodeDefsMap: {},
			publicRunnitNodeDefTags: [],
			publicRunnitNodeDefTagsMap: {},
			runnitKits: [],
			runnitKitsMap: {},
			draftRunnitNodeRun: null,
			runnitState: {
				runnitsOwnerSelection,
				runnitsAccessLevel,
				runnit: null,
				selectedNodeId: null,
				configureRunnitOpen: false,
				runnitManageAccessOpen: false,
				runnitDeleteOpen: false,
				createRunnitTemplateOpen: false,
				duplicateTemplate: false,
				settingsDrawerOpen: false,
				imageGalleryDialogOpen: false,
				imageGalleryDialogInitialNav: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
				selectedImagesDialogOpen: false,
				imageGalleryDrawerOpen: false,
				toolsLibraryDialogOpen: false,
				globalSearchOpen: false,
				globalSearchTrigger: null,
				fieldsByType: _keys(RUNNIT_NODE_FIELD_TYPE).reduce((acc: Record<RunnitNodeFieldType, RunnitNodeField[]>, key: string) => {
					acc[RUNNIT_NODE_FIELD_TYPE[key]] = [];
					return acc;
				}, {} as Record<RunnitNodeFieldType, RunnitNodeField[]>),
				generatedNegativePrompt: null,
				initialHistoryLength: 0,
				inputValues: {},
				staticInputValues: {},
				runnitDraft: null,
				runnitDraftNodeRef: null,
				cloneFromTemplate: false,
				templateRunnit: null,
				deletedImages: {
					nodeRunResults: [],
					uploads: [],
					avatars: [],
				},
				selectedImageMode: SELECTED_IMAGE_MODE.TILED,
				selectedColumns: 3,
				selectedPromptView: SELECTED_PROMPT_VIEW.PROMPT_DRIVEN,
				isQueuingDraftRunnitNodeRun: false,
				promptGeneratorOpen: false,
				imageSelectOpen: false,
				inpaintingEditorStates: {},
				visualPromptEditorStates: {},
				pose3DEditorStates: {},
				imageCache: {},
				videoCache: {},
				imageComparisonMode: false,
				comparisonFieldSourceImageUrls: {},
				comparisonFieldDefUuid: null,
				draftLoadingTimerStartAtMillisMap: {},
				tokenCooldownData: {
					cooldownEndsAt: null,
					cooldownData: null,
					wasBlockedByCooldown: false,
				},
			},
			selectionStateHistory: {},
			modelsState: {
				models: [],
				modelDialog: {
					open: false,
				},
			},
			announcements: [],
			setupSessionPrefill: null,
			recentUserRunnitNodeDefInfos: [],
			recentUserWorkflowRunnitInfos: [],
			companyUserRole: null,
			teamUserRole: null,

			currSessions: [],
			currSessionBackgroundTasks: [],
			pastSessions: [],
			pastSessionsLoading: false,
			pastSessionsCursor: null,

			clubOffers: [],
			clubOffersMap: {},
			appOffers: [],
			appOffersMap: {},
			hardwareOffers: [],
			hardwareOffersMap: {},
			softwareOffers: [],
			softwareOffersMap: {},
			deploymentImages: [],
			deploymentImagesMap: {},
			deploymentImagesByRepoRef: {},
			mobiusEnabledRaw: getMobiusEnabled(),
			adminRegularSessions: [],
			stableDiffusionOffers: [],
			stableDiffusionModels: [],
			stableDiffusionModel: null,
			modelSelectItems: {
				civitai: [],
			},
			preloadModel: {
				civitai: {
					model: null,
					modelVersion: null,
				},
			},

			loader: {
				show: false,
				message: '',
			},
			snackbars: [],
			toolbar: {
				session: null,
				sbudServicesLoading: false,
				sbudServices: {
					sessionId: null,
					downloadCivitaiModels: false,
					restart: false,
					getLogs: false,
					secrets: {},
				},
				fileBrowserOpen: false,
				sessionBarOpen: false,
				sessionBarCollapsed: true,
				serverBuddyOpen: false,
				accountMenuOpen: false,
				changeLoginSettingsDialogConfig: {
					open: false,
					requireSwitchToSso: false,
				},
				mobileMenuOpen: false,
				notificationsOpen: false,
				settingsOpen: false,
				logoutDialogOpen: false,
				needReauthenticateDialogOpen: false,
				runnitsSideNavOpen: false,
				runnitsSideNavCollapsed: false,
				userManuallyCollapsedSideNav: false,
				resourcesDialog: {
					open: false,
					appOffer: null,
				},
				showFailedInitMessage: false,
			},
			deviceReportLocal: null,
			realTimeMoment: moment(),
			sessionSoundsOff,
			isAdminProxyingUser: !!proxyAdminToken,
			triggers: {
				reloadSessionUiIframeTrigger: 0,
				imageZoomResetTrigger: 0,
				fireInitialPromptGenerationTrigger: 0,
				createNewTeamTrigger: 0,
				openRunnitAccountSelectionMenuTrigger: 0,
				toggleRunnitWorkflowAllToolsTrigger: { trigger: 0 },
				promptSelectedModelChangeTrigger: { model: null, trigger: 0 },
				screenResizeBreakpointTrigger: {} as Breakpoint,
			},
			dragAndDrop: {
				isDragging: false,
				draggedItem: null,
				allowDrop: false,
				activeDropZones: 0
			},
		} as State;
	},

	getters: {
		// Single gate for Mobius UI visibility, derived from the persisted user toggle
		// (mobiusEnabledRaw, from localStorage). Mobius is now available to all signed-in
		// users; admin-only protection of unpublished builds is enforced separately by the
		// `isPublished` filter in VuexFirestoreBindingsMixin. All consumers must read
		// `mobiusEnabled` via this getter, never the raw state field.
		mobiusEnabled: (state: State): boolean => !!(state.mobiusEnabledRaw && state.user),
		// Returns the AppKey for a session regardless of cluster.
		// - Legacy sessions: session.apps[0]
		// - Mobius sessions: derived from session.deploymentImage via deploymentImagesByRepoRef
		// Returns null when neither source can resolve.
		sessionAppKey: (state: State) => (session: Session): AppKey | null => {
			if (!session) return null;
			if (session.apps && session.apps.length) return session.apps[0] || null;
			if (session.deploymentImage) {
				const di: DeploymentImage = state.deploymentImagesByRepoRef[session.deploymentImage];
				return (di && di.app) || null;
			}
			return null;
		},
		// Human-readable label for the "software/version" line on a session card.
		// - Legacy sessions: looks up the SoftwareOffer label
		// - Mobius sessions: returns the DeploymentImage label (or repoRef as a final fallback)
		// Returns '' when nothing can be resolved.
		sessionSoftwareLabel: (state: State) => (session: Session): string => {
			if (!session) return '';
			if (session.software) {
				const so = state.softwareOffersMap[session.software];
				return (so && so.shortLabel) || session.software;
			}
			if (session.deploymentImage) {
				const di = state.deploymentImagesByRepoRef[session.deploymentImage];
				if (di) return di.snapshotTitle || di.shortLabel || di.label || di.repoRef;
				return session.deploymentImage;
			}
			return '';
		},
		isLoggedIn: (state: State): boolean => !!(
			state.authUser &&
			state.authUser.uid &&
			state.user &&
			state.authUser.uid === state.user.id
		),
		isLoggingIn: (state: State): boolean => !!(
			!state.hasCheckedAuth ||
			(
				state.authUser &&
				state.authUser.uid &&
				(
					!state.user ||
					state.authUser.uid !== state.user.id
				)
			)
		),
		is90DaysNoActivityNewUserFlow: (state: State): boolean => {
			const daysAgo90: Moment = moment().subtract(90, 'days');
			return !!(
				state.userBalanceAccount &&
				state.user &&
				(
					!state.userBalanceAccount.lastSessionAt ||
					moment(state.userBalanceAccount.lastSessionAt.toMillis()).isBefore(daysAgo90)
				) &&
				(
					!state.userBalanceAccount.lastRunnitNodeRunAt ||
					moment(state.userBalanceAccount.lastRunnitNodeRunAt.toMillis()).isBefore(daysAgo90)
				) &&
				_isEmpty(state.user.teamIds)
			);
		},
		isNewUserTrialFlow: (state: State): boolean => !!(
			state.userBalanceAccount &&
			state.user &&
			!state.userBalanceAccount.lastSessionAt &&
			!state.userBalanceAccount.lastRunnitNodeRunAt &&
			_isEmpty(state.user.teamIds)
		),
		showStripeUserMembershipPortalBtn: (state: State): boolean => !!(
			state.stripeCustomer &&
			state.stripeCustomer.stripeId &&
			state.user &&
			state.user.clubOffer &&
			typeof state.user.clubOffer !== 'string' &&
			!state.user.clubOffer.isFreemium
		),
		showStripeTeamSubscriptionPortalBtn: (state: State): boolean => !!(
			state.stripeTeamsCustomer &&
			state.stripeTeamsCustomer.stripeId &&
			state.team &&
			!state.team.company?.clubOffer?.benefits?.isEnterprise // Subscription is managed outside of Stripe
		),
		isMobileNavOpen: (state: State): boolean => {
			const el: HTMLElement = document.querySelector('#measure-vh');
			if (!el) return false;
			return window.innerHeight < el.clientHeight;
		},
		displayLabels: (state: State): CompanyDisplayLabels => {
			return {
				team: state.team?.company?.displayLabels?.team || 'Team',
			};
		},
		skipNewUserSurvey: (state: State): boolean => {
			return !!state.deviceReportLocal?.skipNewUserSurvey;
		},
		activeDomainClaimRestriction: (state: State): DeviceReportLocal['domainClaimRestriction'] | null => {
			const restriction: DeviceReportLocal['domainClaimRestriction'] = state.deviceReportLocal?.domainClaimRestriction || null;
			if (!restriction) return null;
			if (state.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return state.team?.companyId === restriction.companyId ? null : restriction;
			}
			return restriction;
		},
		userClubOfferBenefits: (state: State): ClubOfferBenefits => {
			return (
				state.user &&
				state.user.clubOffer &&
				typeof state.user.clubOffer !== 'string' &&
				state.user.clubOffer.benefits
			) || {
				isCc: false,
				monthlyRunnitTokens: false,
				downloadModels: false,
				isEnterprise: false,
			};
		},
		companyClubOfferBenefits: (state: State): ClubOfferBenefits => {
			return state.team?.company?.clubOffer?.benefits || {
				isCc: false,
				monthlyRunnitTokens: false,
				downloadModels: false,
				isEnterprise: false,
			};
		},
		canDownloadModels: (state: State): boolean => {
			return !!(
				state.user &&
				state.user.clubOffer &&
				typeof state.user.clubOffer !== 'string' &&
				state.user.clubOffer.benefits &&
				(
					state.user.isAdmin ||
					(
						state.user.clubOffer.benefits.downloadModels &&
						state.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER
					) ||
					(
						state.team &&
						state.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
					)
				)
			);
		},
		needsUnlockRdTrial: (state: State): boolean => {
			return !!(
				state.authUser &&
				state.authUser.emailVerified &&
				state.user &&
				state.user.clubOffer &&
				typeof state.user.clubOffer !== 'string' &&
				state.user.clubOffer.isFreemium &&
				(
					!_get(state.user, 'freemiumInfo.rdTrialAt') &&
					state.user.trial
				) &&
				(
					_get(state.user, 'freemiumInfo.invalidEmail') ||
					_get(state.user, 'freemiumInfo.noFingerprint') ||
					_get(state.user, 'freemiumInfo.dupFingerprint')
				)
			);
		},
		runnitNumNodesLimit: (state: State): number => {
			return getRunnitNumNodesLimit(state.runnitState.runnitsOwnerSelection, _get(state.user, 'clubInfo.limit.numNodesLimit'));
		},
		// Returns a map of runnitNode IDs that are usable with the given plan
		runnitNodesLimitMap: (state: State, getters: any): Record<string, RunnitNode> => {
			return _mapKeys(_slice(state.runnitNodes, 0, getters.runnitNumNodesLimit), 'id');
		},
		/**
		 * Converts the map of selection states into an ordered list
		 * Moved from mixin computed property to Vuex getter for performance
		 */
		orderedSelectionStates: (state: State): SelectionState[] => {
			return getOrderedSelectionStates(state.selectionStateHistory);
		},
		/**
		 * Returns the active selectionState with the highest sort order
		 * Moved from mixin computed property to Vuex getter for performance
		 */
		currentSelectionState: (state: State, getters: any): SelectionState => {
			return getCurrentSelectionStateFromHistory(state.selectionStateHistory, emptySelectionState);
		},
		getSelectionStateByTrigger: (state: State) => (triggeringId: string): SelectionState => {
			return getSelectionStateByTriggerFromHistory(state.selectionStateHistory, triggeringId, emptySelectionState);
		},
		getInpaintingEditorState: (state: State) => (fieldUuid: string) => {
			return state.runnitState.inpaintingEditorStates[fieldUuid] || {
				open: false,
				saving: false,
				backgroundImageUrl: null,
				maskUrl: null,
			};
		},
	},

	mutations: {
		/*
		VuexFirestoreBindingsMixin - Loading & BoundProps
		 */
		setBoundPropsClubOffers: ((state: State, payload: {}) => {
			state.boundPropsClubOffers = payload;
		}),
		setBoundPropsAppOffers: ((state: State, payload: { user: User }) => {
			state.boundPropsAppOffers = payload;
		}),
		setBoundPropsHardwareOffers: ((state: State, payload: { user: User }) => {
			state.boundPropsHardwareOffers = payload;
		}),
		setBoundPropsSoftwareOffers: ((state: State, payload: { user: User }) => {
			state.boundPropsSoftwareOffers = payload;
		}),
		setBoundPropsDeploymentImages: ((state: State, payload: { user: User }) => {
			state.boundPropsDeploymentImages = payload;
		}),
		setMobiusEnabled: ((state: State, mobiusEnabled: boolean) => {
			state.mobiusEnabledRaw = !!mobiusEnabled;
			// Persist so the toggle survives page reloads / re-logins. Without this, users who
			// expect to be in Mobius mode after a refresh will silently get a Legacy launch.
			lsSetMobiusEnabled(!!mobiusEnabled);
		}),
		setBoundPropsStableDiffusionOffers: ((state: State, payload: { user: User }) => {
			state.boundPropsStableDiffusionOffers = payload;
		}),
		setBoundPropsWorkspaces: ((state: State, payload: { teamId: string }) => {
			state.boundPropsWorkspaces = payload;
		}),
		setBoundPropsLimitNumRunnits: ((state: State, payload: { userId: string, teamId: string, accessLevel: RunnitsAccessLevel, limitClubAddOn: LimitClubAddOn }) => {
			state.boundPropsLimitNumRunnits = payload;
		}),
		setBoundPropsRunnitNodes: ((state: State, payload: { runnitId: string }) => {
			state.boundPropsRunnitNodes = payload;
		}),
		setBoundPropsTrainerNodeDefs: ((state: State, payload: {}) => {
			state.boundPropsTrainerNodeDefs = payload;
		}),
		setBoundPropsPublicRunnitNodeDefs: ((state: State, payload: {}) => {
			state.boundPropsPublicRunnitNodeDefs = payload;
		}),
		setBoundPropsCompanyRunnitNodeDefs: ((state: State, payload: { companyId: string }) => {
			state.boundPropsCompanyRunnitNodeDefs = payload;
		}),
		setBoundPropsPublicRunnitNodeDefTags: ((state: State, payload: {}) => {
			state.boundPropsPublicRunnitNodeDefTags = payload;
		}),
		setBoundPropsRunnitKits: ((state: State, payload: {}) => {
			state.boundPropsRunnitKits = payload;
		}),
		setBoundPropsDraftRunnitNodeRun: ((state: State, payload: { userId: string, nodeId: string }) => {
			state.boundPropsDraftRunnitNodeRun = payload;
		}),
		setBoundPropsAnnouncements: ((state: State, payload: {}) => {
			state.boundPropsAnnouncements = payload;
		}),
		setBoundPropsUser: ((state: State, payload: { authUser: FirebaseUser }) => {
			state.boundPropsUser = payload;
		}),
		setBoundPropsUserBalanceAccount: ((state: State, payload: { user: User }) => {
			state.boundPropsUserBalanceAccount = payload;
		}),
		setBoundPropsTeam: ((state: State, payload: { teamId: string }) => {
			state.boundPropsTeam = payload;
		}),
		setBoundPropsTeamBalanceAccount: ((state: State, payload: { teamId: string }) => {
			state.boundPropsTeamBalanceAccount = payload;
		}),
		setBoundPropsStripeCustomer: ((state: State, payload: { user: User }) => {
			state.boundPropsStripeCustomer = payload;
		}),
		setBoundPropsStripeTeamsCustomer: ((state: State, payload: { teamId: string }) => {
			state.boundPropsStripeTeamsCustomer = payload;
		}),
		setBoundPropsCurrSessions: ((state: State, payload: { user: User }) => {
			state.boundPropsCurrSessions = payload;
		}),
		setBoundPropsCurrSessionBackgroundTasks: ((state: State, payload: { session: Session }) => {
			state.boundPropsCurrSessionBackgroundTasks = payload;
		}),
		setBoundPropsAdminRegularSessions: ((state: State, payload: { user: User }) => {
			state.boundPropsAdminRegularSessions = payload;
		}),
		setBoundPropsStableDiffusionModels: ((state: State, payload: {}) => {
			state.boundPropsStableDiffusionModels = payload;
		}),
		setBoundPropsSetupSessionPrefill: ((state: State, payload: { authUser: FirebaseUser, appKey: AppKey }) => {
			state.boundPropsSetupSessionPrefill = payload;
		}),
		setBoundPropsRecentUserRunnitNodeDefInfos: ((state: State, payload: { user: User }) => {
			state.boundPropsRecentUserRunnitNodeDefInfos = payload;
		}),
		setBoundPropsRecentUserWorkflowRunnitInfos: ((state: State, payload: { user: User, team: Team }) => {
			state.boundPropsRecentUserWorkflowRunnitInfos = payload;
		}),
		setBoundPropsModels: ((state: State, payload: { userId: string, teamId: string, accessLevel: RunnitsAccessLevel }) => {
			state.boundPropsModels = payload;
		}),
		setBoundPropsCompanyUserRole: ((state: State, payload: { userId: string, companyId: string }) => {
			state.boundPropsCompanyUserRole = payload;
		}),
		setBoundPropsTeamUserRole: ((state: State, payload: { userId: string, teamId: string }) => {
			state.boundPropsTeamUserRole = payload;
		}),

		/*
		VuexFirestoreBindingsMixin - Actual binding results
		Mutate the object, because vuefire reuses the collection
		 */
		resultClubOffers: ((state: State, payload: ClubOffer[]) => {
			const newArrayPayload: ClubOffer[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.clubOffers = newArrayPayload;
			state.clubOffersMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultAppOffers: ((state: State, payload: AppOffer[]) => {
			const newArrayPayload: AppOffer[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder').map((appOffer: AppOffer) => {
					return buildNewVuefireBindingObj(appOffer, {
						tags: (appOffer.tags || []).sort(),
					});
				}),
			);
			state.appOffers = newArrayPayload;
			state.appOffersMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultHardwareOffers: ((state: State, payload: HardwareOffer[]) => {
			const newArrayPayload: HardwareOffer[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.hardwareOffers = newArrayPayload;
			state.hardwareOffersMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultSoftwareOffers: ((state: State, payload: SoftwareOffer[]) => {
			const newArrayPayload: SoftwareOffer[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			)
				.filter((s: SoftwareOffer) => {
					const isEnterprisePlusFlag: boolean = !!state.team?.features?.isEnterprisePlus;
					return state.user?.isAdmin ||
						(s as any).isMultiRegion ||
						!isEnterprisePlusFlag;
				});
			state.softwareOffers = newArrayPayload;
			state.softwareOffersMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultDeploymentImages: ((state: State, payload: DeploymentImage[]) => {
			const newArrayPayload: DeploymentImage[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.deploymentImages = newArrayPayload;
			state.deploymentImagesMap = _mapKeys(newArrayPayload, 'id');
			state.deploymentImagesByRepoRef = _mapKeys(newArrayPayload, 'repoRef');
		}),
		resultStableDiffusionOffers: ((state: State, payload: StableDiffusionOffer[]) => {
			state.stableDiffusionOffers = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
		}),
		resultWorkspaces: ((state: State, payload: Workspace[]) => {
			const newArrayPayload: Workspace[] = buildNewVuefireBindingArray(payload);
			state.workspaces = newArrayPayload;
			state.workspacesMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultLimitNumRunnits: ((state: State, payload: Runnit[]) => {
			const newArrayPayload: Runnit[] = buildNewVuefireBindingArray(payload);
			state.limitNumRunnits = newArrayPayload;
			state.limitNumRunnitsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultRunnitNodes: ((state: State, payload: RunnitNode[]) => {
			const newArrayPayload: RunnitNode[] = buildNewVuefireBindingArray(payload);
			state.runnitNodes = newArrayPayload;
			state.runnitNodesMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultTrainerNodeDefs: ((state: State, payload: RunnitNodeDef[]) => {
			const newArrayPayload: RunnitNodeDef[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.trainerNodeDefs = newArrayPayload;
			state.trainerNodeDefsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultPublicRunnitNodeDefs: ((state: State, payload: RunnitNodeDef[]) => {
			const newArrayPayload: RunnitNodeDef[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.publicRunnitNodeDefs = newArrayPayload;
			state.publicRunnitNodeDefsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultCompanyRunnitNodeDefs: ((state: State, payload: RunnitNodeDef[]) => {
			const newArrayPayload: RunnitNodeDef[] = buildNewVuefireBindingArray(
				_sortBy(payload || [], 'sortOrder'),
			);
			state.companyRunnitNodeDefs = newArrayPayload;
			state.companyRunnitNodeDefsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultPublicRunnitNodeDefTags: ((state: State, payload: RunnitNodeDefTag[]) => {
			const newArrayPayload: RunnitNodeDefTag[] = buildNewVuefireBindingArray(
				payload.map((tag) => buildNewVuefireBindingObj(tag)),
			);
			state.publicRunnitNodeDefTags = newArrayPayload;
			state.publicRunnitNodeDefTagsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultRunnitKits: ((state: State, payload: RunnitKit[]) => {
			const newArrayPayload: RunnitKit[] = buildNewVuefireBindingArray(
				payload.map((kit) => buildNewVuefireBindingObj(kit)),
			);
			state.runnitKits = newArrayPayload;
			state.runnitKitsMap = _mapKeys(newArrayPayload, 'id');
		}),
		resultDraftRunnitNodeRun: ((state: State, payload: RunnitNodeRun[]) => {
			const firstRunnitNodeRun: RunnitNodeRun = _get(payload, '[0]') || null;
			state.draftRunnitNodeRun = buildNewVuefireBindingObj(firstRunnitNodeRun);
		}),
		resultAnnouncements: ((state: State, payload: Announcement[]) => {
			state.announcements = buildNewVuefireBindingArray(payload);
		}),
		resultUser: ((state: State, payload: User) => {
			state.user = buildNewVuefireBindingObj(payload);
		}),
		resultUserBalanceAccount: ((state: State, payload: BalanceAccount) => {
			state.userBalanceAccount = buildNewVuefireBindingObj(payload);
		}),
		resultTeam: ((state: State, payload: Team) => {
			state.team = buildNewVuefireBindingObj(payload);
		}),
		resultTeamBalanceAccount: ((state: State, payload: BalanceAccount) => {
			state.teamBalanceAccount = buildNewVuefireBindingObj(payload);
		}),
		resultStripeCustomer: ((state: State, payload: StripeCustomer) => {
			state.stripeCustomer = buildNewVuefireBindingObj(payload);
		}),
		resultStripeTeamsCustomer: ((state: State, payload: StripeCustomer) => {
			state.stripeTeamsCustomer = buildNewVuefireBindingObj(payload);
		}),
		resultCurrSessions: ((state: State, payload: Session[]) => {
			state.currSessions = buildNewVuefireBindingArray(payload);
		}),
		resultCurrSessionBackgroundTasks: ((state: State, payload: Session[]) => {
			state.currSessionBackgroundTasks = buildNewVuefireBindingArray(payload);
		}),
		resultAdminRegularSessions: ((state: State, payload: Session[]) => {
			state.adminRegularSessions = buildNewVuefireBindingArray(payload);
		}),
		resultStableDiffusionModels: ((state: State, payload: StableDiffusionModel[]) => {
			state.stableDiffusionModels = buildNewVuefireBindingArray(payload);
		}),
		resultSetupSessionPrefill: ((state: State, payload: SetupSessionPrefill) => {
			state.setupSessionPrefill = buildNewVuefireBindingObj(payload);
		}),
		resultRecentUserRunnitNodeDefInfos: ((state: State, payload: UserRunnitNodeDefInfo[]) => {
			state.recentUserRunnitNodeDefInfos = buildNewVuefireBindingArray(payload);
		}),
		resultRecentUserWorkflowRunnitInfos: ((state: State, payload: UserWorkflowRunnitInfo[]) => {
			state.recentUserWorkflowRunnitInfos = buildNewVuefireBindingArray(payload);
		}),
		resultModels: ((state: State, payload: Model[]) => {
			state.modelsState.models = buildNewVuefireBindingArray(payload);
		}),
		resultCompanyUserRole: ((state: State, payload: CompanyUserRole) => {
			state.companyUserRole = buildNewVuefireBindingObj(payload);
		}),
		resultTeamUserRole: ((state: State, payload: TeamUserRole) => {
			state.teamUserRole = buildNewVuefireBindingObj(payload);
		}),

		/*
		VuexFirestoreBindingsMixin - Set loading booleans for initial fetch
		 */
		setLoadingClubOffers: ((state: State, loading: boolean) => {
			state.loadingClubOffers = !!loading;
		}),
		setLoadingAppOffers: ((state: State, loading: boolean) => {
			state.loadingAppOffers = !!loading;
		}),
		setLoadingHardwareOffers: ((state: State, loading: boolean) => {
			state.loadingHardwareOffers = !!loading;
		}),
		setLoadingSoftwareOffers: ((state: State, loading: boolean) => {
			state.loadingSoftwareOffers = !!loading;
		}),
		setLoadingDeploymentImages: ((state: State, loading: boolean) => {
			state.loadingDeploymentImages = !!loading;
		}),
		setLoadingStableDiffusionOffers: ((state: State, loading: boolean) => {
			state.loadingStableDiffusionOffers = !!loading;
		}),
		setLoadingWorkspaces: ((state: State, loading: boolean) => {
			state.loadingWorkspaces = !!loading;
		}),
		setLoadingLimitNumRunnits: ((state: State, loading: boolean) => {
			state.loadingLimitNumRunnits = !!loading;
		}),
		setLoadingRunnitNodes: ((state: State, loading: boolean) => {
			state.loadingRunnitNodes = !!loading;
		}),
		setPendingRunnit: ((state: State, payload: { id: string, type: string } | null) => {
			state.pendingRunnit = payload;
		}),
		setLoadingTrainerNodeDefs: ((state: State, loading: boolean) => {
			state.loadingTrainerNodeDefs = !!loading;
		}),
		setLoadingPublicRunnitNodeDefs: ((state: State, loading: boolean) => {
			state.loadingPublicRunnitNodeDefs = !!loading;
		}),
		setLoadingCompanyRunnitNodeDefs: ((state: State, loading: boolean) => {
			state.loadingCompanyRunnitNodeDefs = !!loading;
		}),
		setLoadingPublicRunnitNodeDefTags: ((state: State, loading: boolean) => {
			state.loadingPublicRunnitNodeDefTags = !!loading;
		}),
		setLoadingRunnitKits: ((state: State, loading: boolean) => {
			state.loadingRunnitKits = !!loading;
		}),
		setLoadingDraftRunnitNodeRun: ((state: State, loading: boolean) => {
			state.loadingDraftRunnitNodeRun = !!loading;
		}),
		setLoadingAnnouncements: ((state: State, loading: boolean) => {
			state.loadingAnnouncements = !!loading;
		}),
		setLoadingUser: ((state: State, loading: boolean) => {
			state.loadingUser = !!loading;
		}),
		setLoadingUserBalanceAccount: ((state: State, loading: boolean) => {
			state.loadingUserBalanceAccount = !!loading;
		}),
		setLoadingTeam: ((state: State, loading: boolean) => {
			state.loadingTeam = !!loading;
		}),
		setLoadingTeamBalanceAccount: ((state: State, loading: boolean) => {
			state.loadingTeamBalanceAccount = !!loading;
		}),
		setLoadingStripeCustomer: ((state: State, loading: boolean) => {
			state.loadingStripeCustomer = !!loading;
		}),
		setLoadingStripeTeamsCustomer: ((state: State, loading: boolean) => {
			state.loadingStripeTeamsCustomer = !!loading;
		}),
		setLoadingCurrSessions: ((state: State, loading: boolean) => {
			state.loadingCurrSessions = !!loading;
		}),
		setLoadingCurrSessionBackgroundTasks: ((state: State, loading: boolean) => {
			state.loadingCurrSessionBackgroundTasks = !!loading;
		}),
		setLoadingAdminRegularSessions: ((state: State, loading: boolean) => {
			state.loadingAdminRegularSessions = !!loading;
		}),
		setLoadingStableDiffusionModels: ((state: State, loading: boolean) => {
			state.loadingStableDiffusionModels = !!loading;
		}),
		setLoadingSetupSessionPrefill: ((state: State, loading: boolean) => {
			state.loadingSetupSessionPrefill = !!loading;
		}),
		setLoadingRecentUserRunnitNodeDefInfos: ((state: State, loading: boolean) => {
			state.loadingRecentUserRunnitNodeDefInfos = !!loading;
		}),
		setLoadingRecentUserWorkflowRunnitInfos: ((state: State, loading: boolean) => {
			state.loadingRecentUserWorkflowRunnitInfos = !!loading;
		}),
		setLoadingModels: ((state: State, loading: boolean) => {
			state.loadingModels = !!loading;
		}),
		setLoadingCompanyUserRole: ((state: State, loading: boolean) => {
			state.loadingCompanyUserRole = !!loading;
		}),
		setLoadingTeamUserRole: ((state: State, loading: boolean) => {
			state.loadingTeamUserRole = !!loading;
		}),

		/*
		Other
		 */
		setAuthUser (state: State, authUser: FirebaseUser) {
			state.hasCheckedAuth = true;
			state.authUser = authUser || null;
			if (
				!authUser ||
				(state.authUserCredential && authUser.uid !== state.authUserCredential.user.uid)
			) {
				state.authUserCredential = null;
			}
		},
		setAuthUserCredential (state: State, authUserCredential: FirebaseUserCredential) {
			state.authUserCredential = authUserCredential || null;
			state.authUser = (authUserCredential && authUserCredential.user) || null;
		},
		setAdminUserProxyTokens (state: State, { proxyAdminToken, proxyAdminIdToken }) {
			state.isAdminProxyingUser = !!proxyAdminToken;
			if (proxyAdminToken) {
				setProxyAdminToken(proxyAdminToken);
				setProxyAdminIdToken(proxyAdminIdToken);
			} else {
				removeProxyAdminToken();
				removeProxyAdminIdToken();
			}
		},
		updateLoginQueryParamsData (state: State, loginQueryParamsData: LoginQueryParamsDataState) {
			state.loginQueryParamsData = {
				...state.loginQueryParamsData,
				...loginQueryParamsData,
			};
		},
		updateAppQueryParamsData (state: State, appQueryParamsData: AppQueryParamsDataState) {
			state.appQueryParamsData = {
				...state.appQueryParamsData,
				...appQueryParamsData,
			};
		},
		setTeamReportEmbedUrlsData (state: State, teamReportEmbedUrlsData: TeamReportEmbedUrlsDataState) {
			state.teamReportEmbedUrlsData = {
				...state.teamReportEmbedUrlsData,
				...teamReportEmbedUrlsData,
			};
		},
		setTeamReportEmbedUrlsDataLoading (state: State, loading: boolean) {
			state.teamReportEmbedUrlsDataLoading = !!loading;
		},
		setUpsellDialog (state: State, upsellDialog: UpsellDialogDataState) {
			state.upsellDialog = {
				...upsellDialog,
			};
		},
		setShowMqlCalendarDialog (state: State, showMqlCalendarDialog: boolean) {
			state.showMqlCalendarDialog = !!showMqlCalendarDialog;
		},
		updateRunnitState (state: State, runnitState: RunnitState) {
			state.runnitState = {
				...state.runnitState,
				...runnitState,
			};
		},
		setSelectionStateHistory (state: State, selectionStateHistory: Record<string, SelectionState>) {
			state.selectionStateHistory = selectionStateHistory || {};
		},
		cacheRunnitImageUrl (state: State, { filePath, url }: { filePath: string, url: string }) {
			cacheRunnitImageUrlInState(state, { filePath, url });
		},
		cacheRunnitVideoUrl (state: State, { filePath, url }: { filePath: string, url: string }) {
			cacheRunnitVideoUrlInState(state, { filePath, url });
		},
		updateTokenCooldownData (state: State, payload: { isCooldownActive?: boolean, cooldownEndsAt?: Date | null, cooldownData?: any, wasBlockedByCooldown?: boolean }) {
			state.runnitState.tokenCooldownData = {
				...state.runnitState.tokenCooldownData,
				...(payload.cooldownEndsAt !== undefined && { cooldownEndsAt: payload.cooldownEndsAt }),
				...(payload.cooldownData !== undefined && { cooldownData: payload.cooldownData }),
				...(payload.wasBlockedByCooldown !== undefined && { wasBlockedByCooldown: payload.wasBlockedByCooldown }),
			};
		},
		updateInpaintingEditorState (state: State, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, backgroundImageUrl: string | null, maskUrl: string | null }> }) {
			const currentFieldState = state.runnitState.inpaintingEditorStates[fieldUuid] || {
				open: false,
				saving: false,
				backgroundImageUrl: null,
				maskUrl: null,
			};

			state.runnitState = {
				...state.runnitState,
				inpaintingEditorStates: {
					...state.runnitState.inpaintingEditorStates,
					[fieldUuid]: {
						...currentFieldState,
						...editorState,
					},
				},
			};
		},
		removeInpaintingEditorState (state: State, fieldUuid: string) {
			state.runnitState = {
				...state.runnitState,
				inpaintingEditorStates: _omit(state.runnitState.inpaintingEditorStates || {}, fieldUuid),
			};
		},
		updateVisualPromptEditorState (state: State, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, backgroundImageUrl: string | null, baseSource: AnnotationSourceReference | null, replaceImageTarget: ImgsVisualPromptReplaceTarget | null, completedReplacement: { upload: RunnitUpload, replaceImageTarget: ImgsVisualPromptReplaceTarget } | null, resetToOriginalRequest: ImgsVisualPromptReplaceTarget | null }> }) {
			const currentFieldState = state.runnitState.visualPromptEditorStates[fieldUuid] || {
				open: false,
				saving: false,
				backgroundImageUrl: null,
				baseSource: null,
				replaceImageTarget: null,
			};

			state.runnitState = {
				...state.runnitState,
				visualPromptEditorStates: {
					...state.runnitState.visualPromptEditorStates,
					[fieldUuid]: {
						...currentFieldState,
						// A new base image never keeps the previous image's source reference.
						baseSource: 'backgroundImageUrl' in editorState ? null : currentFieldState.baseSource,
						...editorState,
					},
				},
			};
		},
		removeVisualPromptEditorState (state: State, fieldUuid: string) {
			state.runnitState = {
				...state.runnitState,
				visualPromptEditorStates: _omit(state.runnitState.visualPromptEditorStates || {}, fieldUuid),
			};
		},
		updatePose3DEditorState (state: State, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, assetFile: File | null, assetName: string | null }> }) {
			const currentFieldState = state.runnitState.pose3DEditorStates[fieldUuid] || {
				open: false,
				saving: false,
				assetFile: null,
				assetName: null,
			};

			state.runnitState = {
				...state.runnitState,
				pose3DEditorStates: {
					...state.runnitState.pose3DEditorStates,
					[fieldUuid]: {
						...currentFieldState,
						...editorState,
					},
				},
			};
		},
		removePose3DEditorState (state: State, fieldUuid: string) {
			state.runnitState = {
				...state.runnitState,
				pose3DEditorStates: _omit(state.runnitState.pose3DEditorStates || {}, fieldUuid),
			};
		},
		updateToolbar (state: State, toolbar: ToolbarState) {
			const showFailedInitMessage: boolean = !!(
				_has(toolbar, 'showFailedInitMessage')
					? toolbar.showFailedInitMessage
					: (
						state.toolbar.showFailedInitMessage ||
						(
							_get(toolbar, 'session.stopCode') === STOP_CODE.FAILED_INIT &&
							_get(toolbar, 'session.state') === SESSION_STATE.STOP
						)
					)
			);

			const closeAllDrawers: boolean = !!(
				(toolbar.accountMenuOpen && !state.toolbar.accountMenuOpen) ||
				(toolbar.mobileMenuOpen && !state.toolbar.mobileMenuOpen) ||
				(toolbar.settingsOpen && !state.toolbar.settingsOpen) ||
				(toolbar.notificationsOpen && !state.toolbar.notificationsOpen) ||
				(toolbar.serverBuddyOpen && !state.toolbar.serverBuddyOpen) ||
				(_get(toolbar, 'resourcesDialog.open') && !_get(state.toolbar, 'resourcesDialog.open'))
			);

			state.toolbar = {
				...state.toolbar,
				...(closeAllDrawers && {
					accountMenuOpen: false,
					mobileMenuOpen: false,
					settingsOpen: false,
					notificationsOpen: false,
					serverBuddyOpen: false,
					resourcesDialog: {
						open: false,
						appOffer: null,
					},
				}),
				...toolbar,
				showFailedInitMessage,
			};
		},
		closeAllDrawers (state: State) {
			state.toolbar = {
				...state.toolbar,
				accountMenuOpen: false,
				mobileMenuOpen: false,
				settingsOpen: false,
				notificationsOpen: false,
				serverBuddyOpen: false,
				resourcesDialog: {
					open: false,
					appOffer: null,
				},
			};
		},
		updateLoader (state: State, loader: LoaderState) {
			state.loader = {
				...state.loader,
				...loader,
				...(loader && !loader.show && {
					message: null,
				}),
			};
		},
		// Enqueue a new stacked snackbar. Kept backward-compatible with the
		// legacy single-snackbar payload: callers still pass
		// { status, message, show: true, timeout? }. A `show: false` payload is
		// treated as a no-op (dismissal now happens per-item via dismissSnackbar).
		updateSnackbar (state: State, snackbar: SnackbarState) {
			if (snackbar.show === false) {
				return;
			}
			state.snackbars = [
				...state.snackbars,
				{
					id: nextSnackbarId(),
					status: snackbar.status,
					message: snackbar.message,
					timeout: snackbar.timeout,
					snackbarStyle: snackbar.snackbarStyle,
				},
			// Keep only the most recent MAX_SNACKBARS, dropping the oldest.
			].slice(-MAX_SNACKBARS);
		},
		dismissSnackbar (state: State, id: number) {
			state.snackbars = state.snackbars.filter((s) => s.id !== id);
		},
		updatePreloadModel (state: State, { providerKey, result }) {
			state.preloadModel = {
				...state.preloadModel,
				[providerKey]: result || null,
			};
		},
		updateModelSelectItems (state: State, { providerKey, results }) {
			state.modelSelectItems = {
				...state.modelSelectItems,
				[providerKey]: results || [],
			};
		},
		setPastSessions (state: State, sessions: Session[]) {
			state.pastSessions = _uniqBy(sessions || [], 'id');
		},
		setPastSessionsLoading (state: State, loading: boolean) {
			state.pastSessionsLoading = !!loading;
		},
		setPastSessionsCursor (state: State, cursor: QueryDocumentSnapshot) {
			state.pastSessionsCursor = cursor || null;
		},
		setStableDiffusionModel (state: State, stableDiffusionModel: StableDiffusionModel) {
			state.stableDiffusionModel = stableDiffusionModel;
		},
		setDeviceReportLocal (state: State, deviceReportLocal: DeviceReportLocal) {
			state.deviceReportLocal = deviceReportLocal || null;
		},
		tickRealTimeMoment (state: State) {
			state.realTimeMoment = moment();
		},
		setSessionSoundsOff (state: State, sessionSoundsOff: boolean) {
			if (sessionSoundsOff) {
				setSessionSoundsOff();
			} else {
				removeSessionSoundsOff();
			}
			state.sessionSoundsOff = !!sessionSoundsOff;
		},
		setSbudServicesLoading (state: State, loading: boolean) {
			state.toolbar = {
				...state.toolbar,
				sbudServicesLoading: !!loading,
			};
		},
		setSbudServices (state: State, sbudServices: ToolbarSbudServicesState) {
			state.toolbar = {
				...state.toolbar,
				sbudServices,
			};
		},
		incrementReloadSessionUiIframeTrigger (state: State) {
			state.triggers.reloadSessionUiIframeTrigger = state.triggers.reloadSessionUiIframeTrigger + 1;
		},
		incrementImageZoomResetTrigger (state: State) {
			state.triggers.imageZoomResetTrigger = state.triggers.imageZoomResetTrigger + 1;
		},
		incrementFireInitialPromptGenerationTrigger (state: State) {
			state.triggers.fireInitialPromptGenerationTrigger = state.triggers.fireInitialPromptGenerationTrigger + 1;
		},
		incrementCreateNewTeamTrigger (state: State) {
			state.triggers.createNewTeamTrigger = state.triggers.createNewTeamTrigger + 1;
		},
		incrementOpenRunnitAccountSelectionMenuTrigger (state: State) {
			state.triggers.openRunnitAccountSelectionMenuTrigger = state.triggers.openRunnitAccountSelectionMenuTrigger + 1;
		},
		incrementToggleRunnitWorkflowAllToolsTrigger (state: State, { expandAll, collapseAll }: { expandAll?: boolean, collapseAll?: boolean }) {
			state.triggers.toggleRunnitWorkflowAllToolsTrigger = {
				...(expandAll ? { expandAll: true } : {}),
				...(collapseAll ? { collapseAll: true } : {}),
				trigger: state.triggers.toggleRunnitWorkflowAllToolsTrigger.trigger + 1,
			};
		},
		incrementPromptSelectedModelChangeTrigger (state: State, { model, resetTrigger }) {
			state.triggers.promptSelectedModelChangeTrigger = {
				...state.triggers.promptSelectedModelChangeTrigger,
				model,
				trigger: resetTrigger ? 0 : state.triggers.promptSelectedModelChangeTrigger.trigger + 1,
			};
		},
		updateScreenResizeBreakpointTrigger (state: State, vuetifyBreakpoint: Breakpoint) {
			// framework on this object gives a circular reference to breakpoint
			state.triggers.screenResizeBreakpointTrigger = _omit(vuetifyBreakpoint, 'framework');
		},
		updateModelState (state: State, modelState: ModelState) {
			state.modelsState = {
				...state.modelsState,
				...modelState,
			};
		},
		SET_DRAG_STATE (state: State, { isDragging, draggedItem = null }: Partial<State['dragAndDrop']>) {
			state.dragAndDrop.isDragging = isDragging;
			state.dragAndDrop.draggedItem = draggedItem;
		},
		SET_ALLOW_DROP (state: State, allowDrop: boolean) {
			state.dragAndDrop.allowDrop = allowDrop;
		},
		ADD_DROP_ZONE (state: State) {
			state.dragAndDrop.activeDropZones += 1;
		},
		REMOVE_DROP_ZONE (state: State) {
			state.dragAndDrop.activeDropZones = Math.max(0, state.dragAndDrop.activeDropZones - 1);
		},
		SET_DRAGGED_ITEM (state: State, draggedItem: State['dragAndDrop']['draggedItem']) {
			state.dragAndDrop.draggedItem = draggedItem;
		},
		SET_IS_DRAGGING (state: State, isDragging: boolean) {
			state.dragAndDrop.isDragging = isDragging;
		},
		setDraftLoadingTimerStartAt (state: State, { nodeRunId, startAtMillis }: { nodeRunId: string, startAtMillis: number }) {
			// Only set if not already exists to prevent overwriting with newer timestamps
			if (state.runnitState.draftLoadingTimerStartAtMillisMap[nodeRunId]) {
				return;
			}
			state.runnitState = {
				...state.runnitState,
				draftLoadingTimerStartAtMillisMap: {
					...state.runnitState.draftLoadingTimerStartAtMillisMap,
					[nodeRunId]: startAtMillis,
				},
			};
		},
		removeDraftLoadingTimer (state: State, nodeRunId: string) {
			// Only remove if it exists to prevent unnecessary state updates
			if (!state.runnitState.draftLoadingTimerStartAtMillisMap[nodeRunId]) {
				return;
			}
			const { [nodeRunId]: removed, ...rest } = state.runnitState.draftLoadingTimerStartAtMillisMap;
			state.runnitState = {
				...state.runnitState,
				draftLoadingTimerStartAtMillisMap: rest,
			};
		},
		storeLastPagination (state: State, { key, pagination, companyId, teamId, teamMemberId }: {
			key: string,
			pagination: Partial<StoredLastPagination>,
			companyId?: string,
			teamId?: string,
			teamMemberId?: string,
		}) {
			const tooManyKeysNum: number = 50;
			const cleanupOldestKey = (
				map: Record<string, StoredLastPagination>,
				maxKeys: number = tooManyKeysNum,
			): Record<string, StoredLastPagination> => {
				const keys: string[] = _keys(map) as string[];
				if (keys.length > maxKeys) {
					// Find the oldest entry by iso
					let oldestKey: string = keys[0];
					let oldestIso: string = map[oldestKey].iso;

					for (const key of keys) {
						const entryIso: string = map[key].iso;
						if (moment(entryIso).isBefore(moment(oldestIso))) {
							oldestKey = key;
							oldestIso = entryIso;
						}
					}

					// Remove the oldest entry and return updated map
					const cleanedMap = { ...map };
					delete cleanedMap[oldestKey];
					return cleanedMap;
				}
				return map;
			};

			// Add timestamp to pagination
			const paginationWithTimestamp: Partial<StoredLastPagination> = {
				...pagination,
				iso: new Date().toISOString(),
			};

			if (key === 'lastTeamMemberPagination' && teamId && teamMemberId) {
				// Handle nested structure for team member pagination
				const currentMap: Record<string, Record<string, StoredLastPagination>> = state.companyDashboard[key] || {};
				const currentTeamMap: Record<string, StoredLastPagination> = currentMap[teamId] || {};

				// Create updated nested map
				const updatedTeamMap: Record<string, StoredLastPagination> = {
					...currentTeamMap,
					[teamMemberId]: {
						...currentTeamMap[teamMemberId],
						...paginationWithTimestamp,
					},
				};

				// Clean up the team map if needed
				const cleanedTeamMap = cleanupOldestKey(updatedTeamMap);

				const updatedMap: Record<string, Record<string, StoredLastPagination>> = {
					...currentMap,
					[teamId]: cleanedTeamMap,
				};

				state.companyDashboard = {
					...state.companyDashboard,
					[key]: updatedMap,
				};
			} else if (key === 'lastTeamPagination' && teamId) {
				// Handle flat structure for team pagination
				const currentMap: Record<string, StoredLastPagination> = state.companyDashboard[key] || {};

				// Create updated map with new pagination data
				const updatedMap: Record<string, StoredLastPagination> = {
					...currentMap,
					[teamId]: {
						...currentMap[teamId],
						...paginationWithTimestamp,
					},
				};

				// Clean up if needed
				const cleanedMap = cleanupOldestKey(updatedMap);

				state.companyDashboard = {
					...state.companyDashboard,
					[key]: cleanedMap,
				};
			} else if (key === 'lastCompanyPagination' && companyId) {
				// Handle flat structure for company pagination
				const currentMap: Record<string, StoredLastPagination> = state.companyDashboard[key] || {};

				// Create updated map with new pagination data
				const updatedMap: Record<string, StoredLastPagination> = {
					...currentMap,
					[companyId]: {
						...currentMap[companyId],
						...paginationWithTimestamp,
					},
				};

				// Clean up if needed
				const cleanedMap = cleanupOldestKey(updatedMap);

				state.companyDashboard = {
					...state.companyDashboard,
					[key]: cleanedMap,
				};
			} else if (key === 'lastMembersDirectoryPagination' && companyId) {
				// Handle flat structure for the members directory pagination
				const currentMap: Record<string, StoredLastPagination> = state.companyDashboard[key] || {};

				// Create updated map with new pagination data
				const updatedMap: Record<string, StoredLastPagination> = {
					...currentMap,
					[companyId]: {
						...currentMap[companyId],
						...paginationWithTimestamp,
					},
				};

				// Clean up if needed
				const cleanedMap = cleanupOldestKey(updatedMap);

				state.companyDashboard = {
					...state.companyDashboard,
					[key]: cleanedMap,
				};
			} else if (key === 'lastRunnitBoardsPagination' && teamId) {
				// Handle runnitBoardDashboard pagination indexed by teamId
				const currentMap: Record<string, StoredLastPagination> = state.runnitBoardDashboard.lastRunnitBoardsPagination || {};

				// Create updated map with new pagination data
				const updatedMap: Record<string, StoredLastPagination> = {
					...currentMap,
					[teamId]: {
						...currentMap[teamId],
						...paginationWithTimestamp,
					},
				};

				// Clean up if needed
				const cleanedMap = cleanupOldestKey(updatedMap);

				state.runnitBoardDashboard = {
					...state.runnitBoardDashboard,
					lastRunnitBoardsPagination: cleanedMap,
				};
			} else if (key === 'lastRunnitTemplatesPagination' && teamId) {
				// Handle runnitBoardDashboard templates pagination indexed by teamId
				const currentMap: Record<string, StoredLastPagination> = state.runnitBoardDashboard.lastRunnitTemplatesPagination || {};

				// Create updated map with new pagination data
				const updatedMap: Record<string, StoredLastPagination> = {
					...currentMap,
					[teamId]: {
						...currentMap[teamId],
						...paginationWithTimestamp,
					},
				};

				// Clean up if needed
				const cleanedMap = cleanupOldestKey(updatedMap);

				state.runnitBoardDashboard = {
					...state.runnitBoardDashboard,
					lastRunnitTemplatesPagination: cleanedMap,
				};
			}
		},
	},

	actions: {
		/*
		VuexFirestoreBindingsMixin - BoundProps
		 */
		bindClubOffers: (({ commit }) => {
			commit('setBoundPropsClubOffers', {});
		}),
		bindAppOffers: (({ commit }, user: User) => {
			commit('setBoundPropsAppOffers', { user });
		}),
		unbindAppOffers: (({ commit }) => {
			commit('setBoundPropsAppOffers', { user: null });
		}),
		bindHardwareOffers: (({ commit }, user: User) => {
			commit('setBoundPropsHardwareOffers', { user });
		}),
		unbindHardwareOffers: (({ commit }) => {
			commit('setBoundPropsHardwareOffers', { user: null });
		}),
		bindSoftwareOffers: (({ commit }, user: User) => {
			commit('setBoundPropsSoftwareOffers', { user });
		}),
		bindDeploymentImages: (({ commit }, user: User) => {
			commit('setBoundPropsDeploymentImages', { user });
		}),
		unbindDeploymentImages: (({ commit }) => {
			commit('setBoundPropsDeploymentImages', { user: null });
		}),
		setMobiusEnabled: (({ commit }, mobiusEnabled: boolean) => {
			commit('setMobiusEnabled', !!mobiusEnabled);
		}),
		unbindSoftwareOffers: (({ commit }) => {
			commit('setBoundPropsSoftwareOffers', { user: null });
		}),
		bindStableDiffusionOffers: (({ commit }, user: User) => {
			commit('setBoundPropsStableDiffusionOffers', { user });
		}),
		bindWorkspaces: (({ commit }, teamId: string) => {
			commit('setBoundPropsWorkspaces', { teamId });
		}),
		unbindWorkspaces: (({ commit }) => {
			commit('setBoundPropsWorkspaces', { teamId: null });
		}),
		bindLimitNumRunnits: (({ commit }, { userId, teamId, accessLevel, limitClubAddOn }) => {
			commit('setBoundPropsLimitNumRunnits', { userId, teamId, accessLevel, limitClubAddOn });
		}),
		unbindLimitNumRunnits: (({ commit }) => {
			commit('setBoundPropsLimitNumRunnits', { userId: null, teamId: null, accessLevel: null, limitClubAddOn: null });
		}),
		bindRunnitNodes: (({ commit }, { runnitId }) => {
			commit('setBoundPropsRunnitNodes', { runnitId });
		}),
		unbindRunnitNodes: (({ commit }) => {
			commit('setBoundPropsRunnitNodes', { runnitId: null });
		}),
		bindTrainerNodeDefs: (({ commit }) => {
			commit('setBoundPropsTrainerNodeDefs', {});
		}),
		unbindTrainerNodeDefs: (({ commit }) => {
			commit('setBoundPropsTrainerNodeDefs', {});
		}),
		bindPublicRunnitNodeDefs: (({ commit }) => {
			commit('setBoundPropsPublicRunnitNodeDefs', {});
		}),
		unbindPublicRunnitNodeDefs: (({ commit }) => {
			commit('setBoundPropsPublicRunnitNodeDefs', {});
		}),
		bindCompanyRunnitNodeDefs: (({ commit }, companyId: string) => {
			commit('setBoundPropsCompanyRunnitNodeDefs', { companyId });
		}),
		unbindCompanyRunnitNodeDefs: (({ commit }) => {
			commit('setBoundPropsCompanyRunnitNodeDefs', {});
		}),
		bindPublicRunnitNodeDefTags: (({ commit }) => {
			commit('setBoundPropsPublicRunnitNodeDefTags', {});
		}),
		unbindPublicRunnitNodeDefTags: (({ commit }) => {
			commit('setBoundPropsPublicRunnitNodeDefTags', {});
		}),
		bindRunnitKits: (({ commit }) => {
			commit('setBoundPropsRunnitKits', {});
		}),
		unbindRunnitKits: (({ commit }) => {
			commit('setBoundPropsRunnitKits', {});
		}),
		bindDraftRunnitNodeRun: (({ commit }, { userId, nodeId }) => {
			commit('setBoundPropsDraftRunnitNodeRun', { userId, nodeId });
		}),
		unbindDraftRunnitNodeRun: (({ commit }) => {
			commit('setBoundPropsDraftRunnitNodeRun', { userId: null, nodeId: null });
		}),
		bindAnnouncements: (({ commit }) => {
			commit('setBoundPropsAnnouncements', {});
		}),
		bindUser: (({ commit }, authUser: FirebaseUser) => {
			commit('setBoundPropsUser', { authUser });
		}),
		bindUserBalanceAccount: (({ commit }, user: User) => {
			commit('setBoundPropsUserBalanceAccount', { user });
		}),
		bindTeam: (({ commit }, teamId: string) => {
			commit('setBoundPropsTeam', { teamId });
		}),
		unbindTeam: (({ commit }) => {
			commit('setBoundPropsTeam', { teamId: null });
		}),
		bindTeamBalanceAccount: (({ commit }, teamId: string) => {
			commit('setBoundPropsTeamBalanceAccount', { teamId });
		}),
		unbindTeamBalanceAccount: (({ commit }) => {
			commit('setBoundPropsTeamBalanceAccount', { teamId: null });
		}),
		bindStripeCustomer: (({ commit }, user: User) => {
			commit('setBoundPropsStripeCustomer', { user });
		}),
		unbindStripeCustomer: (({ commit }) => {
			commit('setBoundPropsStripeCustomer', { user: null });
		}),
		bindStripeTeamsCustomer: (({ commit }, teamId: string) => {
			commit('setBoundPropsStripeTeamsCustomer', { teamId });
		}),
		unbindStripeTeamsCustomer: (({ commit }) => {
			commit('setBoundPropsStripeTeamsCustomer', { teamId: null });
		}),
		bindCurrSessions: (({ commit }, user: User) => {
			commit('setBoundPropsCurrSessions', { user });
		}),
		bindCurrSessionBackgroundTasks: (({ commit }, session: Session) => {
			commit('setBoundPropsCurrSessionBackgroundTasks', { session });
		}),
		bindAdminRegularSessions: (({ commit }, user: User) => {
			commit('setBoundPropsAdminRegularSessions', { user });
		}),
		bindStableDiffusionModels: (({ commit }) => {
			commit('setBoundPropsStableDiffusionModels', {});
		}),
		bindSetupSessionPrefill: (({ commit }, { authUser, appKey }) => {
			commit('setBoundPropsSetupSessionPrefill', { authUser, appKey });
		}),
		bindRecentUserRunnitNodeDefInfos: (({ commit }, user: User) => {
			commit('setBoundPropsRecentUserRunnitNodeDefInfos', { user });
		}),
		bindRecentUserWorkflowRunnitInfos: (({ commit }, { user, team }) => {
			commit('setBoundPropsRecentUserWorkflowRunnitInfos', { user, team });
		}),
		bindModels: (({ commit }, { userId, teamId, accessLevel }) => {
			commit('setBoundPropsModels', { userId, teamId, accessLevel });
		}),
		unbindModels: (({ commit }) => {
			commit('setBoundPropsModels', { userId: null, teamId: null, accessLevel: null });
		}),
		bindCompanyUserRole: (({ commit }, { userId, companyId }) => {
			commit('setBoundPropsCompanyUserRole', { userId, companyId });
		}),
		unbindCompanyUserRole: (({ commit }) => {
			commit('setBoundPropsCompanyUserRole', { userId: null, companyId: null });
		}),
		bindTeamUserRole: (({ commit }, { userId, teamId }) => {
			commit('setBoundPropsTeamUserRole', { userId, teamId });
		}),
		unbindTeamUserRole: (({ commit }) => {
			commit('setBoundPropsTeamUserRole', { userId: null, teamId: null });
		}),

		/*
		VuexFirestoreBindingsMixin - Actual binding results
		 */
		resultClubOffers: (({ commit }, payload: ClubOffer[]) => {
			commit('resultClubOffers', payload);
		}),
		resultAppOffers: (({ commit }, payload: AppOffer[]) => {
			commit('resultAppOffers', payload);
		}),
		resultHardwareOffers: (({ commit }, payload: HardwareOffer[]) => {
			commit('resultHardwareOffers', payload);
		}),
		resultSoftwareOffers: (({ commit }, payload: SoftwareOffer[]) => {
			commit('resultSoftwareOffers', payload);
		}),
		resultDeploymentImages: (({ commit }, payload: DeploymentImage[]) => {
			commit('resultDeploymentImages', payload);
		}),
		resultStableDiffusionOffers: (({ commit }, payload: StableDiffusionOffer[]) => {
			commit('resultStableDiffusionOffers', payload);
		}),
		resultWorkspaces: (({ commit }, payload: Workspace[]) => {
			commit('resultWorkspaces', payload);
		}),
		resultLimitNumRunnits: (({ commit }, payload: Runnit[]) => {
			commit('resultLimitNumRunnits', payload);
		}),
		resultRunnitNodes: (({ commit }, payload: RunnitNode[]) => {
			commit('resultRunnitNodes', payload);
		}),
		resultTrainerNodeDefs: (({ commit }, payload: RunnitNodeDef[]) => {
			commit('resultTrainerNodeDefs', payload);
		}),
		resultPublicRunnitNodeDefs: (({ commit }, payload: RunnitNodeDef[]) => {
			commit('resultPublicRunnitNodeDefs', payload);
		}),
		resultCompanyRunnitNodeDefs: (({ commit }, payload: RunnitNodeDef[]) => {
			commit('resultCompanyRunnitNodeDefs', payload);
		}),
		resultPublicRunnitNodeDefTags: (({ commit }, payload: RunnitNodeDef[]) => {
			commit('resultPublicRunnitNodeDefTags', payload);
		}),
		resultRunnitKits: (({ commit }, payload: RunnitKit[]) => {
			commit('resultRunnitKits', payload);
		}),
		resultDraftRunnitNodeRun: (({ commit }, payload: RunnitNodeRun[]) => {
			commit('resultDraftRunnitNodeRun', payload);
		}),
		resultAnnouncements: (({ commit }, payload: Announcement[]) => {
			commit('resultAnnouncements', payload);
		}),
		resultUser: (({ commit }, payload: User) => {
			commit('resultUser', payload);
		}),
		resultUserBalanceAccount: (({ commit, state }, payload: BalanceAccount) => {
			maybeNotifyAutoTopUpEvent(commit, state.userBalanceAccount, payload);
			commit('resultUserBalanceAccount', payload);
		}),
		resultTeam: (({ commit }, payload: Team) => {
			commit('resultTeam', payload);
		}),
		resultTeamBalanceAccount: (({ commit, state }, payload: BalanceAccount) => {
			maybeNotifyAutoTopUpEvent(commit, state.teamBalanceAccount, payload);
			commit('resultTeamBalanceAccount', payload);
		}),
		resultStripeCustomer: (({ commit }, payload: StripeCustomer) => {
			commit('resultStripeCustomer', payload);
		}),
		resultStripeTeamsCustomer: (({ commit }, payload: StripeCustomer) => {
			commit('resultStripeTeamsCustomer', payload);
		}),
		resultCurrSessions: (({ commit }, payload: Session[]) => {
			commit('resultCurrSessions', payload);
		}),
		resultCurrSessionBackgroundTasks: (({ commit }, payload: Session[]) => {
			commit('resultCurrSessionBackgroundTasks', payload);
		}),
		resultAdminRegularSessions: (({ commit }, payload: Session[]) => {
			commit('resultAdminRegularSessions', payload);
		}),
		resultStableDiffusionModels: (({ commit }, payload: StableDiffusionModel[]) => {
			commit('resultStableDiffusionModels', payload);
		}),
		resultSetupSessionPrefill: (({ commit }, payload: SetupSessionPrefill) => {
			commit('resultSetupSessionPrefill', payload);
		}),
		resultRecentUserRunnitNodeDefInfos: (({ commit }, payload: UserRunnitNodeDefInfo[]) => {
			commit('resultRecentUserRunnitNodeDefInfos', payload);
		}),
		resultRecentUserWorkflowRunnitInfos: (({ commit }, payload: UserWorkflowRunnitInfo[]) => {
			commit('resultRecentUserWorkflowRunnitInfos', payload);
		}),
		resultModels: (({ commit }, payload: Model[]) => {
			commit('resultModels', payload);
		}),
		resultCompanyUserRole: (({ commit }, payload: CompanyUserRole) => {
			commit('resultCompanyUserRole', payload);
		}),
		resultTeamUserRole: (({ commit }, payload: TeamUserRole) => {
			commit('resultTeamUserRole', payload);
		}),

		/*
		VuexFirestoreBindingsMixin - Set loading booleans for initial fetch
		 */
		setLoadingClubOffers: (({ commit }, loading: boolean) => {
			commit('setLoadingClubOffers', !!loading);
		}),
		setLoadingAppOffers: (({ commit }, loading: boolean) => {
			commit('setLoadingAppOffers', !!loading);
		}),
		setLoadingHardwareOffers: (({ commit }, loading: boolean) => {
			commit('setLoadingHardwareOffers', !!loading);
		}),
		setLoadingSoftwareOffers: (({ commit }, loading: boolean) => {
			commit('setLoadingSoftwareOffers', !!loading);
		}),
		setLoadingDeploymentImages: (({ commit }, loading: boolean) => {
			commit('setLoadingDeploymentImages', !!loading);
		}),
		setLoadingStableDiffusionOffers: (({ commit }, loading: boolean) => {
			commit('setLoadingStableDiffusionOffers', !!loading);
		}),
		setLoadingWorkspaces: (({ commit }, loading: boolean) => {
			commit('setLoadingWorkspaces', !!loading);
		}),
		setLoadingLimitNumRunnits: (({ commit }, loading: boolean) => {
			commit('setLoadingLimitNumRunnits', !!loading);
		}),
		setLoadingRunnitNodes: (({ commit }, loading: boolean) => {
			commit('setLoadingRunnitNodes', !!loading);
		}),
		setPendingRunnit: (({ commit }, payload: { id: string, type: string } | null) => {
			commit('setPendingRunnit', payload);
		}),
		setLoadingTrainerNodeDefs: (({ commit }, loading: boolean) => {
			commit('setLoadingTrainerNodeDefs', !!loading);
		}),
		setLoadingPublicRunnitNodeDefs: (({ commit }, loading: boolean) => {
			commit('setLoadingPublicRunnitNodeDefs', !!loading);
		}),
		setLoadingCompanyRunnitNodeDefs: (({ commit }, loading: boolean) => {
			commit('setLoadingCompanyRunnitNodeDefs', !!loading);
		}),
		setLoadingPublicRunnitNodeDefTags: (({ commit }, loading: boolean) => {
			commit('setLoadingPublicRunnitNodeDefTags', !!loading);
		}),
		setLoadingRunnitKits: (({ commit }, loading: boolean) => {
			commit('setLoadingRunnitKits', !!loading);
		}),
		setLoadingDraftRunnitNodeRun: (({ commit }, loading: boolean) => {
			commit('setLoadingDraftRunnitNodeRun', !!loading);
		}),
		setLoadingAnnouncements: (({ commit }, loading: boolean) => {
			commit('setLoadingAnnouncements', !!loading);
		}),
		setLoadingUser: (({ commit }, loading: boolean) => {
			commit('setLoadingUser', !!loading);
		}),
		setLoadingUserBalanceAccount: (({ commit }, loading: boolean) => {
			commit('setLoadingUserBalanceAccount', !!loading);
		}),
		setLoadingTeam: (({ commit }, loading: boolean) => {
			commit('setLoadingTeam', !!loading);
		}),
		setLoadingTeamBalanceAccount: (({ commit }, loading: boolean) => {
			commit('setLoadingTeamBalanceAccount', !!loading);
		}),
		setLoadingStripeCustomer: (({ commit }, loading: boolean) => {
			commit('setLoadingStripeCustomer', !!loading);
		}),
		setLoadingStripeTeamsCustomer: (({ commit }, loading: boolean) => {
			commit('setLoadingStripeTeamsCustomer', !!loading);
		}),
		setLoadingCurrSessions: (({ commit }, loading: boolean) => {
			commit('setLoadingCurrSessions', !!loading);
		}),
		setLoadingCurrSessionBackgroundTasks: (({ commit }, loading: boolean) => {
			commit('setLoadingCurrSessionBackgroundTasks', !!loading);
		}),
		setLoadingAdminRegularSessions: (({ commit }, loading: boolean) => {
			commit('setLoadingAdminRegularSessions', !!loading);
		}),
		setLoadingStableDiffusionModels: (({ commit }, loading: boolean) => {
			commit('setLoadingStableDiffusionModels', !!loading);
		}),
		setLoadingSetupSessionPrefill: (({ commit }, loading: boolean) => {
			commit('setLoadingSetupSessionPrefill', !!loading);
		}),
		setLoadingRecentUserRunnitNodeDefInfos: (({ commit }, loading: boolean) => {
			commit('setLoadingRecentUserRunnitNodeDefInfos', !!loading);
		}),
		setLoadingRecentUserWorkflowRunnitInfos: (({ commit }, loading: boolean) => {
			commit('setLoadingRecentUserWorkflowRunnitInfos', !!loading);
		}),
		setLoadingModels: (({ commit }, loading: boolean) => {
			commit('setLoadingModels', !!loading);
		}),
		setLoadingCompanyUserRole: (({ commit }, loading: boolean) => {
			commit('setLoadingCompanyUserRole', !!loading);
		}),
		setLoadingTeamUserRole: (({ commit }, loading: boolean) => {
			commit('setLoadingTeamUserRole', !!loading);
		}),

		/*
		Fetches
		 */
		fetchSbudServices: async (ctx: ActionContext<State, State>, sessionId: string): Promise<boolean> => {
			ctx.commit('setSbudServicesLoading', true);

			try {
				const proxyAdminIdToken: string = getProxyAdminIdToken();
				const functionRef = functions
					.httpsCallable('serverBuddy');
				const response = (await functionRef({
					sessionId,
					getAvailableServices: true,
					...(proxyAdminIdToken && { proxyAdminIdToken }),
				})).data;
				if (response && response.success) {
					ctx.commit('setSbudServices', {
						sessionId: sessionId,
						downloadCivitaiModels: !!response.data.downloadCivitaiModels,
						restart: !!response.data.restart,
						getLogs: !!response.data.getLogs,
						secrets: response.data.secrets || {},
					});
					return true;
				}
			} catch (e) {
				console.error(e);
			} finally {
				ctx.commit('setSbudServicesLoading', false);
			}

			return false;
		},
		loadCivitaiPreloadModel: async (ctx: ActionContext<State, State>): Promise<void> => {
			const CIVITAI_GET_MODEL_BASE_URL: string = `https://civitai.com/api/v1/models/`;
			const lastModelId: string = getCivitaiModelId();
			const lastModelVersionId: string = getCivitaiModelVersionId();
			let model: any = null;
			let modelVersion: any = null;

			if (lastModelId && lastModelVersionId) {
				try {
					const response: AxiosResponse = await axios.get(`${CIVITAI_GET_MODEL_BASE_URL}${lastModelId}`);
					if (response && response.data) {
						model = response.data || null;
						modelVersion = (
							(model.modelVersions || []).find(({ id }) => _toString(id) === _toString(lastModelVersionId))
						) || null;
					}
				} catch (e) {
					console.error(e);
				}
			}

			if (model && modelVersion) {
				const providerKey: ModelProviderKey = 'civitai';
				ctx.commit('updatePreloadModel', {
					providerKey,
					result: {
						model,
						modelVersion,
					},
				});
			}
		},
		loadModelSelectItems: async (
			ctx: ActionContext<State, State>,
			searchStr: string,
		): Promise<{ civitaiSuccess: boolean }> => {
			const search: string = _trim(searchStr) || null;
			let civitaiSuccess: boolean = true;

			try {
				const functionRef = functions
					.httpsCallable('getCivitaiModelsList');
				const { success, results } = (await functionRef({
					search,
				})).data;

				const providerKey: ModelProviderKey = 'civitai';
				ctx.commit('updateModelSelectItems', { providerKey, results: success ? results : [] });
			} catch (e) {
				console.error(e);
				civitaiSuccess = false;
			}

			return {
				civitaiSuccess,
			};
		},
		loadMorePastSessions: async (ctx: ActionContext<State, State>): Promise<void> => {
			ctx.commit('setPastSessionsLoading', true);
			const PAGE_SIZE: number = 10;

			// index created: sessions: state Ascending userId Ascending createdAt Descending __name__ Descending
			const whereParams1: [string, WhereFilterOp, any] = ['state', 'in', PAST_SESSION_STATES];
			const whereParams2: [string, WhereFilterOp, any] = ['userId', '==', ctx.state.user.id];
			const orderByParams: [string, OrderByDirection] = ['createdAt', 'desc'];
			const limit: number = PAGE_SIZE + 1;
			const sessionsRef: Query = ctx.state.pastSessionsCursor
				? (
					db.collection('sessions')
						.where(...whereParams1)
						.where(...whereParams2)
						.orderBy(...orderByParams)
						.limit(limit)
						.startAt(ctx.state.pastSessionsCursor)
				) : (
					db.collection('sessions')
						.where(...whereParams1)
						.where(...whereParams2)
						.orderBy(...orderByParams)
						.limit(limit)
				);

			const fetchedSessions: Session[] = [];
			let cursor: QueryDocumentSnapshot = null;
			(await sessionsRef.get()).forEach((snapshot: QueryDocumentSnapshot) => {
				if (fetchedSessions.length < PAGE_SIZE) {
					fetchedSessions.push({
						...snapshot.data(),
						get id () { return snapshot.id },
					} as Session);
				} else {
					cursor = snapshot;
				}
			});

			ctx.commit('setPastSessions', [
				...ctx.state.pastSessions,
				...fetchedSessions,
			]);
			ctx.commit('setPastSessionsLoading', false);
			ctx.commit('setPastSessionsCursor', cursor);
		},
		loadTeamReportEmbedUrls: async (ctx: ActionContext<State, State>, payload: { team: Team, company: Company, teamMemberEmail: string }): Promise<void> => {
			ctx.commit('setTeamReportEmbedUrlsDataLoading', true);
			const onError: Function = (e) => {
				console.error('Error loading the embedUrls: ', e);
			};

			try {
				const {
					team,
					company,
					teamMemberEmail,
				} = payload;

				const functionRef = functions
					.httpsCallable('getTeamReportEmbedUrls');
				const { success, iframeUrls } = (await functionRef({
					teamId: team?.id || null,
					companyId: company?.id || null,
					teamMemberEmail,
				})).data;

				if (!success) {
					onError(new Error('failed to load team report embedUrls'));
				} else {
					ctx.commit('setTeamReportEmbedUrlsData', {
						...ctx.state.teamReportEmbedUrlsData,
						...iframeUrls,
					});
					ctx.commit('setTeamReportEmbedUrlsDataLoading', false);
				}
			} catch (e) {
				onError(e);
			}
		},

		/*
		Auth
		 */
		updateAuthEmail: async (ctx: ActionContext<State, State>, email: string): Promise<{ success: boolean, showReauthenticateDialog: boolean }> => {
			const processedEmail: string = _trim(email).toLowerCase();
			const authUser: FirebaseUser = auth.currentUser;
			try {
				await authUser.updateEmail(processedEmail);

				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Success! Email updated.',
					show: true,
				} as SnackbarState);
				return {
					success: true,
					showReauthenticateDialog: false,
				};
			} catch (error: any) {
				const errorCode: string = error.code;
				console.error('Error updating login email: ', error);

				let message: string = 'Error updating email address, try again.';
				let showReauthenticateDialog: boolean = false;
				if (errorCode === 'auth/email-already-in-use') {
					message = 'Email already in use';
				} else if (errorCode === 'auth/requires-recent-login') {
					showReauthenticateDialog = true;
				}

				if (!showReauthenticateDialog) {
					ctx.commit('updateSnackbar', {
						status: SNACKBAR_STATUS.ERROR,
						message,
						show: true,
					} as SnackbarState);
				}
				return {
					success: false,
					showReauthenticateDialog,
				};
			}
		},

		/*
		Creates
		 */
		purchaseSession: async (
			ctx: ActionContext<State, State>,
			{
				stableDiffusionOffer,
				apps,
				backendApps,
				hardware,
				software,
				deploymentImage,
				options,
				apiEnabled,
				persistExtensions,
				preloadModel,
				purchaseMins,
				stableDiffusionModel,
				displayName,
				isTeamSession,
				workspaceId,
				region,
			}: {
				stableDiffusionOffer?: StableDiffusionOffer
				apps: AppKey[] | null
				backendApps: AppKey[]
				hardware: HardwareKey
				software: SoftwareKey | null
				deploymentImage?: string | null
				options: SessionOptions
				apiEnabled: boolean
				persistExtensions: boolean
				preloadModel: { provider: ModelProviderKey, modelId: string, modelVersionId: string, downloadUrl: string }
				purchaseMins: number
				stableDiffusionModel: StableDiffusionModel
				displayName: string
				isTeamSession: boolean
				workspaceId: string
				region: Region
			},
		): Promise<{
			success: boolean,
			sessionUuid: string,
		}> => {
			// A deactivated team can't start sessions. The Launch button already holds this, and the
			// server stops such a session uncharged (onSessionWrite) if one is written anyway.
			if (isTeamSession && !ctx.state.team?.isActive) {
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: `This ${ctx.getters.displayLabels.team} is deactivated, so it can't launch new sessions.`,
					show: true,
				} as SnackbarState);
				return {
					success: false,
					sessionUuid: null,
				};
			}
			try {
				const nowDate: Date = new Date();
				const userRef: DocumentReference = db.doc(`users/${ctx.state.user.id}`);
				const userEmail: string = ctx.state.user.email;
				const teamRef: DocumentReference = isTeamSession ? db.doc(`teams/${ctx.state.team.id}`) : null;
				const workspaceRef: DocumentReference = workspaceId ? db.doc(`workspaces/${workspaceId}`) : null;
				const balanceAccountRef: DocumentReference = db.doc(`balanceAccounts/${isTeamSession ? ctx.state.team.id : ctx.state.user.id}`);
				const stableDiffusionOfferRef: DocumentReference = stableDiffusionOffer ? db.doc(`stableDiffusionOffers/${stableDiffusionOffer.id}`) : null;
				const stableDiffusionModelRef: DocumentReference = stableDiffusionModel ? db.doc(`stableDiffusionModels/${stableDiffusionModel.id}`) : null;

				const sessionUuid: string = nanoIdLowerNum(9);
				await db.collection('sessions')
					.add({
						type: SESSION_TYPE.RD,
						createdAt: nowDate,
						purchaseAt: nowDate,
						purchaseMins,
						state: SESSION_STATE.PURCHASE,
						uuid: sessionUuid,
						userId: userRef.id,
						user: userRef,
						teamId: isTeamSession ? ctx.state.team.id : null,
						team: isTeamSession ? teamRef : null,
						email: userEmail,
						...(workspaceId && {
							workspaceId: workspaceId,
							workspace: workspaceRef,
						}),
						balanceAccount: balanceAccountRef,
						// Mobius (MOB-1) sessions intentionally omit `apps` and `software` — the deploymentImage is the source of truth.
						// HACK: deployed onSessionWrite (master) derefs `session.apps[0]` and `softwareOffer.releaseStatus`
						// unguarded in the PURCHASE handler. Write dummies on Mobius sessions until the Mobius-aware
						// function ships. `'MOB'` is only used as a Firestore path segment / stored string, no doc lookup.
						// `'SDALL-038'` is an existing softwareOffers doc whose releaseStatus is populated.
						...(apps ? { apps } : (deploymentImage ? { apps: ['MOB'] } : {})),
						backendApps,
						hardware,
						...(software ? { software } : (deploymentImage ? { software: 'SDALL-038' } : {})),
						...(deploymentImage && { deploymentImage }),
						...(!_isEmpty(options) && {
							options
						}),
						apiEnabled,
						persistExtensions,
						preloadModel,
						displayName,
						...(stableDiffusionOfferRef && {
							stableDiffusionOffer: stableDiffusionOfferRef,
						}),
						...(stableDiffusionModelRef && {
							stableDiffusionModel: stableDiffusionModelRef,
						}),
						// Region precedence (bulletproof Mobius routing):
					// 1. If a Mobius deploymentImage is being written, the session MUST go to a
					//    Mobius region — Mobius is the only cluster that knows how to boot a Docker
					//    image directly. We respect MOB-DEV-1 when explicitly passed, otherwise MOB-1.
					// 2. Otherwise the legacy SESSION_REGION_TEST localStorage debug bypass wins
					//    (admins use it to force a region for testing).
					// 3. Otherwise the caller-provided region.
					region: deploymentImage
						? (region === 'MOB-DEV-1' ? 'MOB-DEV-1' : 'MOB-1')
						: (localStorage.getItem('SESSION_REGION_TEST') || region),
					});
				return {
					success: true,
					sessionUuid,
				};
			} catch (e) {
				console.error('Error purchasing session: ', e);
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error purchasing session, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				} as SnackbarState);
				return {
					success: true,
					sessionUuid: null,
				};
			}
		},

		/*
		Updates
		 */
		updateSessionDetails: async (
			ctx: ActionContext<State, State>,
			{ sessionId, updateFields }: { sessionId: string, updateFields: Partial<Session> },
		): Promise<{ success: boolean }> => {
			try {
				const sessionRef: DocumentReference = db.doc(`sessions/${sessionId}`);
				await sessionRef
					.update({
						...updateFields,
					});
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Success! Session updated.',
					show: true,
				} as SnackbarState);
				return { success: true };
			} catch (e) {
				console.error('Error updating session details: ', e);
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving session',
					show: true,
				} as SnackbarState);
				return { success: false };
			}
		},
		extendSession: async (
			ctx: ActionContext<State, State>,
			{ session, extendMins }: { session: Session, extendMins: number },
		): Promise<{ success: boolean }> => {
			const onError: Function = (e) => {
				console.error('Error extending session: ', e);
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error extending session, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				} as SnackbarState);
			};

			try {
				const functionRef = functions
					.httpsCallable('setExtendSession');
				const { success, teamInactive, message } = (await functionRef({
					sessionId: session.id,
					extendMins,
				})).data;
				if (!success && teamInactive) {
					// The session's team is deactivated: say so as it is, not as an error to report
					ctx.commit('updateSnackbar', {
						status: SNACKBAR_STATUS.ERROR,
						message: message || `This ${ctx.getters.displayLabels.team} is deactivated, so it can't extend sessions.`,
						show: true,
					} as SnackbarState);
				} else if (!success) {
					onError(new Error('setExtendSession returned success: false'));
				}

				return { success };
			} catch (e) {
				onError(e);
				return { success: false };
			}
		},
		stopSession: async (ctx: ActionContext<State, State>, session: Session): Promise<{ success: boolean }> => {
			const onError: Function = (e) => {
				console.error('Error stopping session: ', e);
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error stopping session, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				} as SnackbarState);
			};

			try {
				const functionRef = functions
					.httpsCallable('setStopSession');
				const { success } = (await functionRef({
					sessionId: session.id,
				})).data;
				if (!success) {
					onError(new Error('setStopSession returned success: false'));
				}

				return { success };
			} catch (e) {
				onError(e);
				return { success: false };
			}
		},

		/*
		Other actions
		 */
		goToStripeSubscriptionPortal: async (ctx: ActionContext<State, State>, team: Team): Promise<{ success: boolean }> => {
			if (
				(!team && (!ctx.state.user || !ctx.state.stripeCustomer)) ||
				(team && (!ctx.state.user || !ctx.state.stripeTeamsCustomer))
			) {
				return { success: false };
			}
			try {
				ctx.commit('updateLoader', {
					show: true,
					message: team ? 'Loading team plan subscription page...' : 'Loading your subscription page...',
				});

				const functionRef = functions
					.httpsCallable('createStripePortalLink');
				const { url } = (await functionRef({
					teamId: team ? team.id : null,
					returnUrl: window.location.href,
				})).data;
				if (url) {
					window.location.href = url;
					return { success: true };
				} else {
					ctx.commit('updateSnackbar', {
						status: SNACKBAR_STATUS.ERROR,
						message: 'Error loading payment account portal, please reach out to report issues by going to Resources -> Help from the menu options',
						show: true,
					});
				}
			} catch (e) {
				console.error(e);
				ctx.commit('updateLoader', {
					show: false,
				});
				ctx.commit('updateSnackbar', {
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading payment account portal, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
				return { success: false };
			}
		},

		/*
		Mutating store
		 */
		setAuthUser ({ commit }, payload: FirebaseUser) {
			commit('setAuthUser', payload);
		},
		async setAuthUserCredential ({ commit }, payload: FirebaseUserCredential) {
			commit('setAuthUserCredential', payload);

			// unlink old providerIds
			if (payload?.additionalUserInfo?.providerId && payload?.user?.providerData?.length) {
				const providerDatasToUnlink: FirebaseUserInfo[] = payload.user.providerData
					.filter(({ providerId }) => providerId !== payload.additionalUserInfo.providerId);
				await asyncForEach(providerDatasToUnlink, async ({ providerId }) => {
					try {
						await payload.user.unlink(providerId);
					} catch (e) {
						console.error(e);
					}
				});
			}
		},
		setAdminUserProxyTokens ({ commit }, payload: { proxyAdminToken: string, proxyAdminIdToken: string }) {
			commit('setAdminUserProxyTokens', payload);
		},
		updateLoginQueryParamsData ({ commit }, payload: Partial<LoginQueryParamsDataState>) {
			commit('updateLoginQueryParamsData', payload);
		},
		updateAppQueryParamsData ({ commit }, payload: Partial<AppQueryParamsDataState>) {
			commit('updateAppQueryParamsData', payload);
		},
		setTeamReportEmbedUrlsData ({ commit }, payload: Partial<TeamReportEmbedUrlsDataState>) {
			commit('setTeamReportEmbedUrlsData', payload);
		},
		setTeamReportEmbedUrlsLoading ({ commit }, payload: Partial<TeamReportEmbedUrlsDataState>) {
			commit('setTeamReportEmbedUrlsLoading', payload);
		},
		setUpsellDialog ({ commit }, payload: Partial<UpsellDialogDataState>) {
			commit('setUpsellDialog', payload);
		},
		setShowMqlCalendarDialog ({ commit }, payload: boolean) {
			commit('setShowMqlCalendarDialog', payload);
		},
		updateRunnitState ({ commit }, payload: Partial<RunnitState>) {
			commit('updateRunnitState', payload);
		},
		setSelectionStateHistory ({ commit }, payload: Record<string, SelectionState>) {
			commit('setSelectionStateHistory', payload);
		},
		cacheRunnitImageUrl ({ commit }, payload: { filePath: string, url: string }) {
			commit('cacheRunnitImageUrl', payload);
		},
		cacheRunnitVideoUrl ({ commit }, payload: { filePath: string, url: string }) {
			commit('cacheRunnitVideoUrl', payload);
		},
		updateTokenCooldownData ({ commit }, payload: { isCooldownActive?: boolean, cooldownEndsAt?: Date | null, cooldownData?: any, wasBlockedByCooldown?: boolean }) {
			commit('updateTokenCooldownData', payload);
		},
		updateInpaintingEditorState ({ commit }, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, backgroundImageUrl: string | null, maskUrl: string | null }> }) {
			commit('updateInpaintingEditorState', { fieldUuid, editorState });
		},
		removeInpaintingEditorState ({ commit }, fieldUuid: string) {
			commit('removeInpaintingEditorState', fieldUuid);
		},
		updateVisualPromptEditorState ({ commit }, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, backgroundImageUrl: string | null, baseSource: AnnotationSourceReference | null, replaceImageTarget: ImgsVisualPromptReplaceTarget | null, completedReplacement: { upload: RunnitUpload, replaceImageTarget: ImgsVisualPromptReplaceTarget } | null, resetToOriginalRequest: ImgsVisualPromptReplaceTarget | null }> }) {
			commit('updateVisualPromptEditorState', { fieldUuid, editorState });
		},
		removeVisualPromptEditorState ({ commit }, fieldUuid: string) {
			commit('removeVisualPromptEditorState', fieldUuid);
		},
		updatePose3DEditorState ({ commit }, { fieldUuid, editorState }: { fieldUuid: string, editorState: Partial<{ open: boolean, saving: boolean, assetFile: File | null, assetName: string | null }> }) {
			commit('updatePose3DEditorState', { fieldUuid, editorState });
		},
		removePose3DEditorState ({ commit }, fieldUuid: string) {
			commit('removePose3DEditorState', fieldUuid);
		},
		updateToolbar ({ commit }, payload: Partial<ToolbarState>) {
			commit('updateToolbar', payload);
		},
		closeAllDrawers ({ commit }) {
			commit('closeAllDrawers');
		},
		updateLoader ({ commit }, payload: Partial<LoaderState>) {
			commit('updateLoader', payload);
		},
		updateSnackbar ({ commit }, payload: Partial<SnackbarState>) {
			commit('updateSnackbar', payload);
		},
		dismissSnackbar ({ commit }, id: number) {
			commit('dismissSnackbar', id);
		},
		updatePreloadModel ({ commit }, { providerKey, result }) {
			commit('updatePreloadModel', { providerKey, result });
		},
		setStableDiffusionModel ({ commit }, payload: StableDiffusionModel) {
			commit('setStableDiffusionModel', payload);
		},
		setDeviceReportLocal ({ commit }, payload: DeviceReportLocal) {
			commit('setDeviceReportLocal', payload);
		},
		tickRealTimeMoment ({ commit }) {
			commit('tickRealTimeMoment');
		},
		setSessionSoundsOff ({ commit }, payload: boolean) {
			commit('setSessionSoundsOff', payload);
		},
		incrementReloadSessionUiIframeTrigger ({ commit }) {
			commit('incrementReloadSessionUiIframeTrigger');
		},
		incrementImageZoomResetTrigger ({ commit }) {
			commit('incrementImageZoomResetTrigger');
		},
		incrementFireInitialPromptGenerationTrigger ({ commit }) {
			commit('incrementFireInitialPromptGenerationTrigger');
		},
		incrementCreateNewTeamTrigger ({ commit }) {
			commit('incrementCreateNewTeamTrigger');
		},
		incrementOpenRunnitAccountSelectionMenuTrigger ({ commit }) {
			commit('incrementOpenRunnitAccountSelectionMenuTrigger');
		},
		incrementToggleRunnitWorkflowAllToolsTrigger ({ commit }, payload: { expandAll?: boolean, collapseAll?: boolean }) {
			commit('incrementToggleRunnitWorkflowAllToolsTrigger', payload);
		},
		incrementPromptSelectedModelChangeTrigger ({ commit }, payload: { model: Model, resetTrigger?: boolean }) {
			commit('incrementPromptSelectedModelChangeTrigger', payload);
		},
		updateScreenResizeBreakpointTrigger ({ commit }, payload: Breakpoint) {
			commit('updateScreenResizeBreakpointTrigger', payload);
		},
		updateModelState ({ commit }, payload: Partial<ModelState>) {
			commit('updateModelState', payload);
		},
		startDrag ({ commit }, draggedItem: State['dragAndDrop']['draggedItem']) {
			commit('SET_DRAG_STATE', { isDragging: true, draggedItem });
		},
		endDrag ({ commit }) {
			commit('SET_DRAG_STATE', { isDragging: false, draggedItem: null });
		},
		setAllowDrop ({ commit }, allowDrop: boolean) {
			commit('SET_ALLOW_DROP', allowDrop);
		},
		addDropZone ({ commit }) {
			commit('ADD_DROP_ZONE');
		},
		removeDropZone ({ commit }) {
			commit('REMOVE_DROP_ZONE');
		},
		setDraggedItem ({ commit }, draggedItem: State['dragAndDrop']['draggedItem']) {
			commit('SET_DRAGGED_ITEM', draggedItem);
		},
		setIsDragging ({ commit }, isDragging: boolean) {
			commit('SET_IS_DRAGGING', isDragging);
		},
		setDraftLoadingTimerStartAt ({ commit }, { nodeRunId, startAtMillis }: { nodeRunId: string, startAtMillis: number }) {
			commit('setDraftLoadingTimerStartAt', { nodeRunId, startAtMillis });
		},
		removeDraftLoadingTimer ({ commit }, nodeRunId: string) {
			commit('removeDraftLoadingTimer', nodeRunId);
		},
		storeLastPagination ({ commit }, { key, pagination, companyId, teamId, teamMemberId }: {
			key: string,
			pagination: Partial<StoredLastPagination>,
			companyId?: string,
			teamId?: string,
			teamMemberId?: string,
		}) {
			commit('storeLastPagination', { key, pagination, companyId, teamId, teamMemberId });
		},

		/*
		Team data binding actions
		 */
		bindAllTeamData ({ dispatch }, teamId: string) {
			dispatch('bindTeam', teamId);
			dispatch('bindStripeTeamsCustomer', teamId);
			dispatch('bindTeamBalanceAccount', teamId);
			dispatch('bindWorkspaces', teamId);
		},
		unbindAllTeamData ({ dispatch }) {
			dispatch('unbindTeam');
			dispatch('unbindStripeTeamsCustomer');
			dispatch('unbindTeamBalanceAccount');
			dispatch('unbindWorkspaces');
		},
	},
	modules: {
		modelUpload: modelUploadModule,
		featureTour: featureTourModule,
	},
});
